import { Switch, Route, Router as WouterRouter, useLocation, useRoute, Redirect } from "wouter";
import { Component, useDeferredValue, useEffect, useState, lazy as reactLazy, Suspense, type ReactNode } from "react";

// Error boundary that catches lazy-chunk load failures (and any other
// uncaught render errors below it) and offers the user a "reload page"
// escape hatch. Without this, a single chunk that fails to download
// after a stale-deploy + cooldown window would unmount the entire
// React tree, leaving the user staring at a blank white page with no
// idea what to do — losing the sale.
class VisibleErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error) {
    try { console.error("[nexus-cart] google-shopping render error:", error); } catch {}
  }
  render() {
    if (!this.state.error) return this.props.children;
    const err = this.state.error;
    return (
      <div style={{ padding: 24, background: "#fff0f0", border: "2px solid red", borderRadius: 8, margin: 16, fontFamily: "monospace", direction: "ltr" }}>
        <div style={{ color: "red", fontSize: 18, fontWeight: "bold", marginBottom: 8 }}>🔴 RENDER CRASH: {err.name}</div>
        <div style={{ color: "#900", fontSize: 14, marginBottom: 12, wordBreak: "break-all" }}>{err.message}</div>
        <pre style={{ fontSize: 11, color: "#555", whiteSpace: "pre-wrap", maxHeight: 300, overflow: "auto", background: "#fee", padding: 8, borderRadius: 4 }}>{err.stack}</pre>
        <button type="button" onClick={() => { try { navigator.clipboard.writeText(`${err.name}: ${err.message}\n\n${err.stack}`); } catch {} alert("Copied!"); }} style={{ marginTop: 8, padding: "4px 12px", background: "#900", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}>Copy Error</button>
      </div>
    );
  }
}

class ChunkErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error) {
    // Best-effort: report to console so it shows up in DevTools and Sentry.
    try { console.error("[nexus-cart] route error caught:", error); } catch {}
    // The #1 cause of this boundary firing is a stale browser tab: after a
    // deploy, Vite emits new chunk hashes and the previous chunks are deleted,
    // so a tab opened BEFORE the deploy throws "Failed to fetch dynamically
    // imported module" the moment the user navigates to a not-yet-loaded lazy
    // route (e.g. category edit). index.html is served no-cache, so a single
    // reload pulls the fresh bundle and the error disappears. Auto-recover so
    // the user never has to manually click Reload — but guard against an
    // infinite reload loop when the chunk is genuinely unreachable (offline,
    // truly missing, or a real render bug rather than a stale deploy).
    try {
      const msg = `${error?.name || ""} ${error?.message || ""}`;
      const isChunkError = /dynamically imported module|module script failed|importing a module script|ChunkLoadError|Loading chunk|Failed to fetch/i.test(msg);
      if (isChunkError && typeof window !== "undefined") {
        const KEY = "__nx_chunk_reload_at";
        const last = Number(window.sessionStorage.getItem(KEY) || "0");
        if (Date.now() - last > 15000) {
          window.sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
        }
      }
    } catch {}
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center bg-slate-50">
        <div className="text-5xl">⚠️</div>
        <div className="text-lg font-semibold text-slate-800">حدث خطأ غير متوقع</div>
        <div className="text-sm text-slate-500">An unexpected error occurred while loading this page.</div>
        <div className="flex gap-3 mt-2">
          <button
            type="button"
            onClick={() => { try { window.location.reload(); } catch {} }}
            className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-700"
          >إعادة تحميل / Reload</button>
          <button
            type="button"
            onClick={() => { try { window.location.href = "/"; } catch {} }}
            className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm hover:bg-slate-100"
          >الصفحة الرئيسية / Home</button>
        </div>
      </div>
    );
  }
}

// ===== Lazy with auto-reload on stale chunk after deploy + idle preload =====
// When a new version is deployed, the user's cached index.html may reference
// chunk filenames that no longer exist. The dynamic import then throws and
// Suspense gets stuck on the loader forever. We catch that and reload the
// page once to fetch the fresh index.html + new chunk hashes.
//
// IDLE PRELOAD: every lazy() call registers its factory in PRELOADERS. After
// the first page paints + the browser goes idle, we trigger every factory in
// the background so the chunk is cached in memory. Result: when the user
// clicks any nav link, the corresponding chunk resolves synchronously —
// Suspense never trips its fallback, and the white "page → blank → page"
// flash on every navigation is gone. We cache the promise per-factory so
// React.lazy and our preload share the same fetch (no double-download).
const CHUNK_RELOAD_KEY = "__nx_chunk_reload_ts";
const PRELOADERS = new Set<() => Promise<any>>();
function lazy<T extends { default: any }>(factory: () => Promise<T>) {
  let cached: Promise<T> | null = null;
  // Clear the cache on rejection so that a later navigation retries the
  // import instead of reusing a permanently-failed promise. Without this,
  // a transient preload failure (network hiccup during idle preloading)
  // permanently bricks every route that uses this lazy() instance.
  const get = () => {
    if (!cached) cached = factory().catch(err => { cached = null; throw err; });
    return cached;
  };
  PRELOADERS.add(get);
  return reactLazy(async () => {
    try {
      const mod = await get();
      try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch {}
      return mod;
    } catch (err: any) {
      const msg = String(err?.message || err || "");
      const isChunkError =
        /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk|Loading CSS chunk|error loading dynamically imported module/i.test(msg);
      if (isChunkError) {
        try {
          const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || "0");
          // Only reload once per minute to avoid infinite reload loops.
          if (Date.now() - last > 60_000) {
            sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
            window.location.reload();
            // Return a never-resolving promise so Suspense stays in fallback
            // until the reload kicks in.
            return await new Promise<T>(() => {});
          }
        } catch {}
      }
      throw err;
    }
  });
}
import MarketingShell from "@/components/marketing/MarketingShell";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useGetSettings } from "@workspace/api-client-react";
import { Toaster } from "@/components/ui/toaster";
import { SubscriptionGate } from "@/lib/subscription-gate";
import { EntitlementsProvider, RequireFeature } from "@/lib/entitlements";
import { TooltipProvider } from "@/components/ui/tooltip";
import { HelpModeProvider } from "@/lib/help-mode-context";
import { AuthProvider } from "@/lib/auth-context";
import { TurnstileProvider } from "@/lib/turnstile-context";
import { RiyalSymbolSwapper } from "@/lib/riyal-symbol-swapper";
import { WishlistProvider } from "@/lib/wishlist-context";
import { CompareProvider } from "@/lib/compare-context";
import { I18nProvider } from "@/lib/i18n";
import { NexusCartProvider } from "@/lib/nexus-cart";
import { CurrencyProvider } from "@/lib/currency";
import { ThemeProvider } from "@/lib/theme-context";
import { ThemeEngineProvider } from "@/lib/theme-engine";
import { DarkModeProvider } from "@/lib/dark-mode";
import { captureStoreRef } from "@/lib/store-ref";

import { StoreLayout } from "@/components/StoreLayout";
import PlatformSeoManager from "@/components/PlatformSeoManager";
import { CompareBar } from "@/components/CompareBar";
import { PushOptIn } from "@/components/PushOptIn";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { isPlatformHost, isCanonicalPlatformHost, redirectToPlatform } from "@/lib/platform-host";

// ===== Critical storefront pages (eager — first paint) =====
// Only Home stays eager (it IS the LCP screen). Shop / ProductDetail /
// NotFound are loaded on demand to keep ~93KB of unused JS off the
// homepage critical path (PageSpeed mobile flagged this directly).
import Home from "@/pages/home";
const Shop = lazy(() => import("@/pages/shop"));
const ProductDetail = lazy(() => import("@/pages/product"));
const NotFound = lazy(() => import("@/pages/not-found"));

// ===== Lazy: less-critical storefront pages =====
const CartPage = lazy(() => import("@/pages/cart"));
const Checkout = lazy(() => import("@/pages/checkout"));
const OrderConfirmation = lazy(() => import("@/pages/order-confirmation"));
const PaymentHyperpay = lazy(() => import("@/pages/payment-hyperpay"));
const PaymentAuctionDeposit = lazy(() => import("@/pages/payment-auction-deposit"));
const PaymentAuctionWinner = lazy(() => import("@/pages/payment-auction-winner"));
const PaymentPaylink = lazy(() => import("@/pages/payment-paylink"));
const PaymentNeoleap = lazy(() => import("@/pages/payment-neoleap"));
const PaymentTamara = lazy(() => import("@/pages/payment-tamara"));
const PaymentTabby = lazy(() => import("@/pages/payment-tabby"));
const PaymentStcPay = lazy(() => import("@/pages/payment-stc-pay"));
const PaymentPayfort = lazy(() => import("@/pages/payment-payfort"));
const SplitPay = lazy(() => import("@/pages/split-pay"));
const SplitPaymentTracker = lazy(() => import("@/pages/split-payment-tracker"));
const Login = lazy(() => import("@/pages/login"));
const Register = lazy(() => import("@/pages/register"));
const Account = lazy(() => import("@/pages/account"));
const WishlistPage = lazy(() => import("@/pages/wishlist"));
const ComparePage = lazy(() => import("@/pages/compare"));
const Invoice = lazy(() => import("@/pages/invoice"));
const CreditNote = lazy(() => import("@/pages/credit-note"));
const ShippingLabel = lazy(() => import("@/pages/shipping-label"));
const PackingList = lazy(() => import("@/pages/packing-list"));
const PrintOrderItem = lazy(() => import("@/pages/print-order-item"));
const Contact = lazy(() => import("@/pages/contact"));
const OrderTracking = lazy(() => import("@/pages/order-tracking"));
const BrandsPage = lazy(() => import("@/pages/brands"));
const CategoriesPage = lazy(() => import("@/pages/categories"));
const FAQPage = lazy(() => import("@/pages/faq"));
const GiftCertificates = lazy(() => import("@/pages/gift-certificates"));
const AffiliateProgram = lazy(() => import("@/pages/affiliate-program"));
const ReferralPage = lazy(() => import("@/pages/referral"));
const SitemapPage = lazy(() => import("@/pages/sitemap"));
const NewArrivalsPage = lazy(() => import("@/pages/new-arrivals"));
const AuctionsPage = lazy(() => import("@/pages/auctions"));
const OfferPage = lazy(() => import("@/pages/offer"));
const InfoPage = lazy(() => import("@/pages/info-page"));
const PrivacyPolicy = lazy(() => import("@/pages/legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/legal/TermsOfService"));
const DataDeletion = lazy(() => import("@/pages/legal/DataDeletion"));
const CookiePolicy = lazy(() => import("@/pages/legal/CookiePolicy"));
const AcceptableUse = lazy(() => import("@/pages/legal/AcceptableUse"));
const MerchantAgreement = lazy(() => import("@/pages/legal/MerchantAgreement"));
const SecurityStatement = lazy(() => import("@/pages/legal/SecurityStatement"));
const PartnerAgreement = lazy(() => import("@/pages/legal/PartnerAgreement"));
const BlogPage = lazy(() => import("@/pages/blog"));
const BlogPostPage = lazy(() => import("@/pages/blog-post"));
const AcademyIndex = lazy(() => import("@/pages/academy/index"));
const AcademyCourse = lazy(() => import("@/pages/academy/course"));
const AcademyLesson = lazy(() => import("@/pages/academy/lesson"));
const AcademyCertificateVerify = lazy(() => import("@/pages/academy/certificate-verify"));
const Welcome = lazy(() => import("@/pages/welcome"));
const Pricing = lazy(() => import("@/pages/pricing"));
const Signup = lazy(() => import("@/pages/signup"));

// PlatformOnlyRoute — wraps a route component and short-circuits BEFORE
// the component (or any of its hooks) ever mounts when we're on a
// tenant host instead of the platform marketing host (nexuscart.co).
// This is required for /pricing and /signup because their content is
// branded for the SaaS, not for any individual merchant — letting them
// render under tenant chrome (banner/header/footer) confuses visitors
// and damages brand consistency. The pre-mount guard in main.tsx
// handles the initial cold load; this wrapper handles in-SPA navigation
// (e.g. a stray link click). Avoids the rules-of-hooks violation that
// would arise from putting the early `return null` inside the page
// component body before its useState calls.
function PlatformOnlyRoute({ path, component: Component }: { path: string; component: React.ComponentType }) {
  if (typeof window !== "undefined" && !isPlatformHost()) {
    redirectToPlatform(path);
    return null;
  }
  return <Component />;
}
const Features = lazy(() => import("@/pages/features"));
const AboutPage = lazy(() => import("@/pages/about"));
const PlatformApps = lazy(() => import("@/pages/apps"));
const MarketingContact = lazy(() => import("@/pages/marketing-contact"));
const MarketingBookMeeting = lazy(() => import("@/pages/marketing-book-meeting"));
const MarketingLogin = lazy(() => import("@/pages/marketing-login"));
const MarketingNotFound = lazy(() => import("@/pages/marketing-not-found"));

// ===== Lazy: Platform admin (super-admin only) =====
const PlatformLogin = lazy(() => import("@/pages/platform/login"));
const PlatformDashboard = lazy(() => import("@/pages/platform/dashboard"));
const PlatformMerchants = lazy(() => import("@/pages/platform/merchants"));
const PlatformMerchantDetail = lazy(() => import("@/pages/platform/merchant-detail"));
const PlatformPackages = lazy(() => import("@/pages/platform/packages"));
const PlatformInvoices = lazy(() => import("@/pages/platform/invoices"));
const PlatformMessageTemplates = lazy(() => import("@/pages/platform/message-templates"));
const PlatformAudit = lazy(() => import("@/pages/platform/audit"));
const PlatformCustomers = lazy(() => import("@/pages/platform/customers"));
const PlatformStorageUsage = lazy(() => import("@/pages/platform/storage-usage"));
const PlatformBlog = lazy(() => import("@/pages/platform-blog"));
const PlatformBlogPost = lazy(() => import("@/pages/platform-blog-post"));
const PlatformBlogPosts = lazy(() => import("@/pages/platform/blog-posts"));
const PlatformBlogPostEdit = lazy(() => import("@/pages/platform/blog-post-edit"));
const PlatformBlogCategories = lazy(() => import("@/pages/platform/blog-categories"));
const PlatformBlogSettings = lazy(() => import("@/pages/platform/blog-settings"));
const PlatformPaymentProviders = lazy(() => import("@/pages/platform/payment-providers"));
const PlatformShippingProviders = lazy(() => import("@/pages/platform/shipping-providers"));
const PlatformPosIntegrations = lazy(() => import("@/pages/platform/pos-integrations"));
const PlatformPayouts = lazy(() => import("@/pages/platform/payouts"));
const PlatformThemeEarnings = lazy(() => import("@/pages/platform/theme-earnings"));
const PlatformBackups = lazy(() => import("@/pages/platform/backups"));
const PlatformHelpOverrides = lazy(() => import("@/pages/platform/help-overrides"));
const PlatformEmailSuppressions = lazy(() => import("@/pages/platform/email-suppressions"));
const PlatformSeo = lazy(() => import("@/pages/platform/seo"));
const PlatformZatca = lazy(() => import("@/pages/platform/zatca-registrations"));
const PlatformPartners = lazy(() => import("@/pages/platform/partners"));
const PlatformTrialSettings = lazy(() => import("@/pages/platform/trial-settings"));
const PlatformBrandAssets = lazy(() => import("@/pages/platform/brand-assets"));
const PlatformTeamMembers = lazy(() => import("@/pages/platform/team-members"));
const PlatformThemeReview = lazy(() => import("@/pages/platform/theme-review"));
const PlatformThemeReviewDetail = lazy(() => import("@/pages/platform/theme-review-detail"));
const TrialSignup = lazy(() => import("@/pages/trial-signup"));
const Onboarding = lazy(() => import("@/pages/onboarding"));
const PlatformPendingApprovals = lazy(() => import("@/pages/platform/pending-approvals"));
const PlatformPendingApprovalDetail = lazy(() => import("@/pages/platform/pending-approval-detail"));
const PlatformDocumentTypes = lazy(() => import("@/pages/platform/document-types"));
const PartnerAuth = lazy(() => import("@/pages/partners/PartnerAuth"));
const PartnerDashboard = lazy(() => import("@/pages/partners/PartnerDashboard"));
const AdminShippingProvidersV2 = lazy(() => import("@/pages/admin/shipping-providers-v2"));
const AdminCarrierRules = lazy(() => import("@/pages/admin/carrier-rules"));
const AdminShippingOtoReports = lazy(() => import("@/pages/admin/shipping-oto-reports"));
const AdminPosProviders = lazy(() => import("@/pages/admin/pos-providers"));
const AdminIntegrationsPos = lazy(() => import("@/pages/admin/integrations-pos"));

// ===== Lazy: Supplier / Logistics portals =====
const SupplierLogin = lazy(() => import("@/pages/supplier/login"));
const SupplierRegister = lazy(() => import("@/pages/supplier/register"));
const SupplierPortal = lazy(() => import("@/pages/supplier/portal"));
const LogisticsLogin = lazy(() => import("@/pages/logistics/login"));
const LogisticsPortal = lazy(() => import("@/pages/logistics/portal"));

// ===== Lazy: Admin (huge — never loaded for storefront visitors) =====
const AdminLayout = lazy(() => import("@/components/AdminLayout").then(m => ({ default: m.AdminLayout }))) as React.ComponentType<{ children: React.ReactNode }>;
const CommandPalette = lazy(() => import("@/components/CommandPalette").then(m => ({ default: m.CommandPalette })));
const AdminLogin = lazy(() => import("@/pages/admin/login"));
const AdminUpgrade = lazy(() => import("@/pages/admin/upgrade"));
const AdminForgotPassword = lazy(() => import("@/pages/admin/forgot-password"));
const AdminResetPassword = lazy(() => import("@/pages/admin/reset-password"));
const AdminVerifyEmail = lazy(() => import("@/pages/admin/verify-email"));
const AdminForceChangePassword = lazy(() => import("@/pages/admin/force-change-password"));
const AdminDashboard = lazy(() => import("@/pages/admin/dashboard"));
const AdminProducts = lazy(() => import("@/pages/admin/products"));
const AdminProductsBulk = lazy(() => import("@/pages/admin/products-bulk"));
const AdminImportFromCompetitor = lazy(() => import("@/pages/admin/import-from-competitor"));
const AdminIntegrations = lazy(() => import("@/pages/admin/integrations"));
const AdminNexusStore = lazy(() => import("@/pages/admin/nexus-store"));
const NexusStoreLanding = lazy(() => import("@/pages/nexus-store/landing"));
const NexusStoreBrowse = lazy(() => import("@/pages/nexus-store/marketplace"));
const NexusStoreCategories = lazy(() => import("@/pages/nexus-store/categories"));
const NexusStoreProduct = lazy(() => import("@/pages/nexus-store/product"));
const NexusStoreMerchant = lazy(() => import("@/pages/nexus-store/merchant"));
const NexusStoreCart = lazy(() => import("@/pages/nexus-store/cart"));
const NexusStoreCheckout = lazy(() => import("@/pages/nexus-store/checkout"));
const NexusStoreBlog = lazy(() => import("@/pages/nexus-store/blog"));
const PlatformNexusStore = lazy(() => import("@/pages/platform/nexus-store"));
const PlatformNexusStoreCategories = lazy(() => import("@/pages/platform/nexus-store-categories"));
const AdminProductEdit = lazy(() => import("@/pages/admin/product-edit"));
const AdminCategories = lazy(() => import("@/pages/admin/categories"));
const AdminTags = lazy(() => import("@/pages/admin/tags"));
const TagPage = lazy(() => import("@/pages/tag"));
const AdminCategoryEdit = lazy(() => import("@/pages/admin/category-edit"));
const AdminOrders = lazy(() => import("@/pages/admin/orders"));
const AdminOrderDetail = lazy(() => import("@/pages/admin/order-detail"));
const AdminCustomers = lazy(() => import("@/pages/admin/customers"));
const AdminCustomerDetail = lazy(() => import("@/pages/admin/customer-detail"));
const AdminCoupons = lazy(() => import("@/pages/admin/coupons"));
const AdminBanners = lazy(() => import("@/pages/admin/banners"));
const AdminBrands = lazy(() => import("@/pages/admin/brands"));
const AdminSizeGuides = lazy(() => import("@/pages/admin/size-guides"));
const AdminSuppliers = lazy(() => import("@/pages/admin/suppliers"));
const AdminSupplierDetail = lazy(() => import("@/pages/admin/supplier-detail"));
const AdminMtoOrders = lazy(() => import("@/pages/admin/mto-orders"));
const LogisticsUsersAdmin = lazy(() => import("@/pages/admin/logistics-users"));
const PurchaseOrdersAdmin = lazy(() => import("@/pages/admin/purchase-orders"));
const DeliveriesAdmin = lazy(() => import("@/pages/admin/deliveries"));
const LogisticsDashboard = lazy(() => import("@/pages/admin/logistics-dashboard"));
const ExpenseCategoriesAdmin = lazy(() => import("@/pages/admin/expense-categories"));
const AccountingHub = lazy(() => import("@/pages/admin/accounting"));
const AdminStoreFeatures = lazy(() => import("@/pages/admin/store-features"));
const AdminReturns = lazy(() => import("@/pages/admin/returns"));
const AdminMultiOffers = lazy(() => import("@/pages/admin/multi-offers"));
const AdminAuctions = lazy(() => import("@/pages/admin/auctions"));
const AdminSettings = lazy(() => import("@/pages/admin/settings"));
const AdminAuthSettings = lazy(() => import("@/pages/admin/auth-settings"));
const AdminDashboardSettings = lazy(() => import("@/pages/admin/dashboard-settings"));
const AdminCustomDomain = lazy(() => import("@/pages/admin/custom-domain"));
const AdminDataPrivacy = lazy(() => import("@/pages/admin/data-privacy"));
const AccountPrivacy = lazy(() => import("@/pages/account-privacy"));
const AccountQuotes = lazy(() => import("@/pages/account-quotes"));
const AdminReviews = lazy(() => import("@/pages/admin/reviews"));
const AdminInfoPages = lazy(() => import("@/pages/admin/info-pages"));
const AdminApps = lazy(() => import("@/pages/admin/apps"));
const AdminAppsMarketplace = lazy(() => import("@/pages/admin/apps-marketplace"));
const AdminAppDetail = lazy(() => import("@/pages/admin/app-detail"));
const AdminAppStore = lazy(() => import("@/pages/admin/app-store"));
const AdminPersonalTokens = lazy(() => import("@/pages/admin/personal-tokens"));
const AdminOAuthConsent = lazy(() => import("@/pages/admin/oauth-consent"));
const AdminReports = lazy(() => import("@/pages/admin/reports"));
const AdminAnalytics = lazy(() => import("@/pages/admin/analytics"));
const AdminGoogleAnalytics = lazy(() => import("@/pages/admin/google-analytics"));
const AdminVisitorLog = lazy(() => import("@/pages/admin/visitor-log"));
const ReportsSales = lazy(() => import("@/pages/admin/reports-sales"));
const ReportsProducts = lazy(() => import("@/pages/admin/reports-products"));
const ReportsCustomers = lazy(() => import("@/pages/admin/reports-customers"));
const ReportsMarketing = lazy(() => import("@/pages/admin/reports-marketing"));
const ReportsOrders = lazy(() => import("@/pages/admin/reports-orders"));
const ReportsReturns = lazy(() => import("@/pages/admin/reports-returns"));
const AdminExtensions = lazy(() => import("@/pages/admin/extensions"));
const AdminConnectHub = lazy(() => import("@/pages/admin/connect-hub"));
const AdminConnectHubWebhooks = lazy(() => import("@/pages/admin/connect-hub-webhooks"));
const AdminConnectHubApiKeys = lazy(() => import("@/pages/admin/connect-hub-api-keys"));
const AdminCorporateRequests = lazy(() => import("@/pages/admin/corporate-requests"));
const AdminCorporateGiftsSettings = lazy(() => import("@/pages/admin/corporate-gifts-settings"));
const AdminHrLeaves = lazy(() => import("@/pages/admin/hr-leaves"));
const CorporateGifts = lazy(() => import("@/pages/corporate-gifts"));
const AdminFileManager = lazy(() => import("@/pages/admin/file-manager"));
const AdminNotificationsPage = lazy(() => import("@/pages/admin/notifications"));
const AdminNotificationSettingsPage = lazy(() => import("@/pages/admin/notification-settings"));
const AdminCustomerNotificationSettingsPage = lazy(() => import("@/pages/admin/customer-notification-settings"));
const AdminSenderDomainsPage = lazy(() => import("@/pages/admin/sender-domains"));
const AdminMessagingProvidersPage = lazy(() => import("@/pages/admin/messaging-providers"));
const PlatformSystemPerformance = lazy(async () => {
  const [page, layoutMod] = await Promise.all([
    import("@/pages/platform/system-performance"),
    import("@/components/PlatformLayout"),
  ]);
  const Page = page.default;
  const { PlatformLayout } = layoutMod;
  return { default: () => <PlatformLayout><Page /></PlatformLayout> };
});
function SystemPerformanceTenantRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => { navigate("/platform-admin/system-performance", { replace: true }); }, [navigate]);
  return null;
}
const AdminSystemMonitoring = lazy(() => import("@/pages/admin/system-monitoring"));
const PlatformSystemMonitoring = lazy(async () => {
  const [page, layoutMod] = await Promise.all([
    import("@/pages/admin/system-monitoring"),
    import("@/components/PlatformLayout"),
  ]);
  const Page = page.default;
  const { PlatformLayout } = layoutMod;
  return { default: () => <PlatformLayout><Page /></PlatformLayout> };
});
function SystemMonitoringTenantRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => { navigate("/platform-admin/system-monitoring", { replace: true }); }, [navigate]);
  return null;
}
const PlatformScheduledJobs = lazy(async () => {
  const [page, layoutMod] = await Promise.all([
    import("@/pages/platform/scheduled-jobs"),
    import("@/components/PlatformLayout"),
  ]);
  const Page = page.default;
  const { PlatformLayout } = layoutMod;
  return { default: () => <PlatformLayout><Page /></PlatformLayout> };
});
const PlatformShippingBilling = lazy(async () => {
  const [page, layoutMod] = await Promise.all([
    import("@/pages/platform/shipping-billing"),
    import("@/components/PlatformLayout"),
  ]);
  const Page = page.default;
  const { PlatformLayout } = layoutMod;
  return { default: () => <PlatformLayout><Page /></PlatformLayout> };
});
function ScheduledJobsTenantRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => { navigate("/platform-admin/scheduled-jobs", { replace: true }); }, [navigate]);
  return null;
}
const AdminCustomCss = lazy(() => import("@/pages/admin/custom-css"));
const AdminConversionTracking = lazy(() => import("@/pages/admin/conversion-tracking"));
const AdminCapiLogs = lazy(() => import("@/pages/admin/capi-logs"));
const AdminSecurity2FA = lazy(() => import("@/pages/admin/security-2fa"));
function TrashRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => { navigate("/admin/file-manager", { replace: true }); }, [navigate]);
  return null;
}
function CategoryRedirect({ id }: { id?: string }) {
  const [, navigate] = useLocation();
  useEffect(() => {
    const safe = id && /^[0-9]+$/.test(id) ? id : "";
    navigate(safe ? `/shop?category=${safe}` : "/shop", { replace: true });
  }, [id, navigate]);
  return null;
}
const AdminPriceHistory = lazy(() => import("@/pages/admin/price-history"));
const AdminBulkActions = lazy(() => import("@/pages/admin/bulk-actions"));
const ExtensionEdit = lazy(() => import("@/pages/admin/extension-edit"));
const AdminGiftVouchers = lazy(() => import("@/pages/admin/gift-vouchers"));
const AdminShipments = lazy(() => import("@/pages/admin/shipments"));
const AdminCustomerGroups = lazy(() => import("@/pages/admin/customer-groups"));
const AdminCustomerFields = lazy(() => import("@/pages/admin/customer-fields"));
const AdminGoogleShopping = lazy(() => import("@/pages/admin/google-shopping"));
const AdminFacebookCatalog = lazy(() => import("@/pages/admin/facebook-catalog"));
const AdminSnapchatCatalog = lazy(() => import("@/pages/admin/snapchat-catalog"));
const AdminTiktokCatalog = lazy(() => import("@/pages/admin/tiktok-catalog"));
const AdminAffiliates = lazy(() => import("@/pages/admin/affiliates"));
const AdminEmailCampaigns = lazy(() => import("@/pages/admin/email-campaigns"));
const AdminSmsCampaigns = lazy(() => import("@/pages/admin/sms-campaigns"));
const AdminCommunity = lazy(() => import("@/pages/admin/community"));
const AdminCommunityPost = lazy(() => import("@/pages/admin/community-post"));
const PlatformCommunity = lazy(() => import("@/pages/platform/community"));
const RoadmapPage = lazy(() => import("@/pages/roadmap"));
const AdminRoadmap = lazy(() => import("@/pages/admin/roadmap"));
const PlatformRoadmap = lazy(() => import("@/pages/platform/roadmap"));
const AdminBlogSettings = lazy(() => import("@/pages/admin/blog-settings"));
const AdminBlogCategories = lazy(() => import("@/pages/admin/blog-categories"));
const AdminBlogPosts = lazy(() => import("@/pages/admin/blog-posts"));
const AdminBlogComments = lazy(() => import("@/pages/admin/blog-comments"));
const AdminContactMessages = lazy(() => import("@/pages/admin/contact-messages"));
const AdminSeo = lazy(() => import("@/pages/admin/seo"));
const AdminAppearance = lazy(() => import("@/pages/admin/appearance"));
const AdminThemes: React.LazyExoticComponent<React.ComponentType<{ initialTab?: "market" | "library" }>> =
  lazy(() => import("@/pages/admin/themes"));
const AdminThemePreview = lazy(() => import("@/pages/admin/theme-preview"));
const AdminThemesPurchases = lazy(() => import("@/pages/admin/themes-purchases"));
const AdminThemeCustomize = lazy(() => import("@/pages/admin/theme-customize"));
const AdminMobileMenu = lazy(() => import("@/pages/admin/mobile-menu"));
const AdminAbandonedCarts = lazy(() => import("@/pages/admin/abandoned-carts"));
const AdminWhatsAppAi = lazy(() => import("@/pages/admin/whatsapp-ai"));
const AdminSiteChat = lazy(() => import("@/pages/admin/site-chat"));
const AdminWhatsAppCampaigns = lazy(() => import("@/pages/admin/whatsapp-campaigns"));
const AdminActivityLog = lazy(() => import("@/pages/admin/activity-log"));
const AdminMarketingTracking = lazy(() => import("@/pages/admin/marketing-tracking"));
const AdminLayouts = lazy(() => import("@/pages/admin/layouts"));
const AdminHomepageLayout = lazy(() => import("@/pages/admin/homepage-layout"));
const AdminPageLayoutsHub = lazy(() => import("@/pages/admin/page-layouts"));
const AdminPageLayoutEditor = lazy(() => import("@/pages/admin/page-layout-editor"));
const AdminCustomPages = lazy(() => import("@/pages/admin/custom-pages"));
const CustomPageView = lazy(() => import("@/pages/custom-page"));
const AdminModules = lazy(() => import("@/pages/admin/modules"));
const AdminProductExtras = lazy(() => import("@/pages/admin/product-extras"));
const AdminWarehouses = lazy(() => import("@/pages/admin/warehouses"));
const AdminCompanies = lazy(() => import("@/pages/admin/companies"));
const AdminQuotes = lazy(() => import("@/pages/admin/quotes"));
const AdminContentStaging = lazy(() => import("@/pages/admin/content-staging"));
const AdminPricingRules = lazy(() => import("@/pages/admin/pricing-rules"));
const AdminAISearch = lazy(() => import("@/pages/admin/ai-search"));
const AdminInventory = lazy(() => import("@/pages/admin/inventory"));
const AdminPackaging = lazy(() => import("@/pages/admin/packaging"));
const AdminInventoryCounts = lazy(() => import("@/pages/admin/inventory-counts"));
const AdminInventoryCountSession = lazy(() => import("@/pages/admin/inventory-count-session"));
const AdminLoyalty = lazy(() => import("@/pages/admin/loyalty"));
const AdminLoyaltyHub = lazy(() => import("@/pages/admin/loyalty-hub"));
const AdminPos = lazy(() => import("@/pages/admin/pos"));
const AdminBi = lazy(() => import("@/pages/admin/bi"));
const AdminAiStudio = lazy(() => import("@/pages/admin/ai-studio"));
const InfluencersPage = lazy(() => import("@/pages/influencers"));
const AdminProductQA = lazy(() => import("@/pages/admin/product-qa"));
const AdminProductOptionsCatalog = lazy(() => import("@/pages/admin/product-options-catalog"));
const AdminBundles = lazy(() => import("@/pages/admin/bundles"));
const AdminCompanionLists = lazy(() => import("@/pages/admin/companion-lists"));
const AdminPopupSettings = lazy(() => import("@/pages/admin/popup-settings"));
const AdminWhatsAppSettings = lazy(() => import("@/pages/admin/whatsapp-settings"));
const AdminDarkModeSettings = lazy(() => import("@/pages/admin/dark-mode-settings"));
const AdminInvoiceSettings = lazy(() => import("@/pages/admin/invoice-settings"));
const AdminTaxSystem = lazy(() => import("@/pages/admin/tax-system"));
const AdminTaxCompliance = lazy(() => import("@/pages/admin/tax-compliance"));
const AdminTrust = lazy(() => import("@/pages/admin/trust"));
const PlatformTrustSignals = lazy(() => import("@/pages/platform/trust-signals"));
const AdminZatcaOnboarding = lazy(() => import("@/pages/admin/zatca-onboarding"));
const AdminAdminGroups = lazy(() => import("@/pages/admin/admin-groups"));
const AdminAttributes = lazy(() => import("@/pages/admin/attributes"));
const AdminFilterGroups = lazy(() => import("@/pages/admin/filter-groups"));
const AdminSidebarFilters = lazy(() => import("@/pages/admin/sidebar-filters"));
const AdminFilterGroupEdit = lazy(() => import("@/pages/admin/filter-group-edit"));
const AdminEmailTemplates = lazy(() => import("@/pages/admin/email-templates"));
const AdminReturnConfig = lazy(() => import("@/pages/admin/return-config"));
const AdminOrderSettings = lazy(() => import("@/pages/admin/order-settings"));
const AdminAntiFraud = lazy(() => import("@/pages/admin/anti-fraud"));
function GdprToDataPrivacyRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => { navigate("/admin/data-privacy", { replace: true }); }, [navigate]);
  return null;
}
const PlatformErrorLogs = lazy(() => import("@/pages/platform/error-logs"));
function ErrorLogsTenantRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => { navigate("/platform-admin/error-logs", { replace: true }); }, [navigate]);
  return null;
}
const AdminDbBackup = lazy(() => import("@/pages/admin/db-backup"));
const AdminSubscriptions = lazy(() => import("@/pages/admin/subscriptions"));
const AdminStockStatuses = lazy(() => import("@/pages/admin/stock-statuses"));
const AdminOrderTotals = lazy(() => import("@/pages/admin/order-totals"));
const AdminStores = lazy(() => import("@/pages/admin/stores"));
const AdminLinkedStores = lazy(() => import("@/pages/admin/linked-stores"));
const AdminMailQueue = lazy(() => import("@/pages/admin/mail-queue"));
const AdminMessagingLogs = lazy(() => import("@/pages/admin/messaging-logs"));
const AdminMessagingOptouts = lazy(() => import("@/pages/admin/messaging-optouts"));
const AdminEventHooks = lazy(() => import("@/pages/admin/event-hooks"));
const AdminLanguages = lazy(() => import("@/pages/admin/languages"));
const AdminMarketplace = lazy(() => import("@/pages/admin/marketplace"));
const PlatformMeetingSettings = lazy(() => import("@/pages/platform/meeting-settings"));
const PlatformMeetingQuestions = lazy(() => import("@/pages/platform/meeting-questions"));
const PlatformMeetingBookings = lazy(() => import("@/pages/platform/meeting-bookings"));
const PlatformMeetingMarketers = lazy(() => import("@/pages/platform/meeting-marketers"));
const PlatformCountries = lazy(async () => {
  const [page, layoutMod] = await Promise.all([
    import("@/pages/platform/countries"),
    import("@/components/PlatformLayout"),
  ]);
  const Page = page.default;
  const { PlatformLayout } = layoutMod;
  return { default: () => <PlatformLayout><Page /></PlatformLayout> };
});
function CountriesTenantRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => { navigate("/platform-admin/countries", { replace: true }); }, [navigate]);
  return null;
}
const AdminOrderStatuses = lazy(() => import("@/pages/admin/order-statuses"));
const PlatformWeightLengthClasses = lazy(async () => {
  const [page, layoutMod] = await Promise.all([
    import("@/pages/platform/weight-length-classes"),
    import("@/components/PlatformLayout"),
  ]);
  const Page = page.default;
  const { PlatformLayout } = layoutMod;
  return { default: () => <PlatformLayout><Page /></PlatformLayout> };
});
function WeightLengthClassesTenantRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => { navigate("/platform-admin/weight-length-classes", { replace: true }); }, [navigate]);
  return null;
}
const AdminGroupedProducts = lazy(() => import("@/pages/admin/grouped-products"));
const AdminCustomerSegments = lazy(() => import("@/pages/admin/customer-segments"));
const AdminRelatedRules = lazy(() => import("@/pages/admin/related-rules"));
const AdminProductAlerts = lazy(() => import("@/pages/admin/product-alerts"));
const AdminComparisonReport = lazy(() => import("@/pages/admin/comparison-report"));
const AdminExportImportCenter = lazy(() => import("@/pages/admin/export-import-center"));
const AdminUrlRewrites = lazy(() => import("@/pages/admin/url-rewrites"));
const AdminCacheManagement = lazy(() => import("@/pages/admin/cache-management"));
const PlatformCacheManagement = lazy(() => import("@/pages/platform/cache-management"));
const AdminIndexer = lazy(() => import("@/pages/admin/indexer"));
const AdminFlashSales = lazy(() => import("@/pages/admin/flash-sales"));
const AdminBundleOffers = lazy(() => import("@/pages/admin/bundle-offers"));
const AdminAutoReviews = lazy(() => import("@/pages/admin/auto-reviews"));
const AdminReferralProgram = lazy(() => import("@/pages/admin/referral-program"));
const AdminPushNotifications = lazy(() => import("@/pages/admin/push-notifications"));
const AdminWishlistAnalytics = lazy(() => import("@/pages/admin/wishlist-analytics"));
const AdminCampaignROI = lazy(() => import("@/pages/admin/campaign-roi"));
const AdminNewArrivals = lazy(() => import("@/pages/admin/new-arrivals"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Tracks whether the very first PageLoader has been instantiated yet.
// The first PageLoader is the Suspense fallback for the *initial* page
// load — it must paint IMMEDIATELY so the user doesn't see a blank
// white gap between the index.html `__boot` overlay disappearing
// (which happens the moment React mounts anything into `#root`) and
// the real route chunk finishing its fetch. Every PageLoader after
// the first is a SPA navigation, where the 350 ms invisible grace
// kills the disappear-then-reappear flash on fast cached chunks.
// THE bug behind "كل ما أفتح أو أحدّث الصفحة تظهر ثم تختفي تماماً ثم
// ترجع" (May 2026, luxurygive.com): on refresh the user saw the boot
// spinner, then a blank white screen for ~350 ms (PageLoader hidden
// during its grace) while #root was technically non-empty so __boot
// was already hidden, then the real content. Making the first
// PageLoader instant closes that gap.
let __firstPageLoaderShown = false;
function PageLoader() {
  const [showLoader, setShowLoader] = useState(!__firstPageLoaderShown);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    __firstPageLoaderShown = true;
    const stuckT = setTimeout(() => setStuck(true), 8000);
    if (showLoader) return () => clearTimeout(stuckT);
    const showT = setTimeout(() => setShowLoader(true), 350);
    return () => { clearTimeout(showT); clearTimeout(stuckT); };
  }, []);
  if (!showLoader) return null;
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-slate-400 px-4 text-center">
      <div>جارٍ التحميل…</div>
      {stuck && (
        <button
          type="button"
          onClick={() => { try { window.location.reload(); } catch {} }}
          className="mt-2 px-4 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-700 transition-colors"
        >
          إعادة تحميل الصفحة / Reload page
        </button>
      )}
    </div>
  );
}

function PlatformLegacyRedirect() {
  const [location, navigate] = useLocation();
  useEffect(() => {
    const target = location.replace(/^\/platform/, "/platform-admin") || "/platform-admin";
    navigate(target, { replace: true });
  }, [location, navigate]);
  return <div className="p-8 text-center text-gray-500">جارٍ التحويل...</div>;
}

// Redirect /admin/stock-ledger → /admin/inventory?tab=history.
// The standalone stock-ledger page was duplicated by the richer
// "سجل الحركات" tab inside the unified Inventory hub, so the bare
// URL now redirects there.
function StockLedgerToInventoryRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate("/admin/inventory?tab=history", { replace: true });
  }, [navigate]);
  return <div className="p-8 text-center text-gray-500">جارٍ التحويل إلى إدارة المخزون...</div>;
}

// Redirect any /admin/inventory-counts visit to the unified location
// /admin/inventory?tab=counts so the feature is not duplicated in
// two separate URL paths. (Per-session URLs /admin/inventory-counts/:id
// keep working; only the bare list URL redirects.)
function InventoryCountsListRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate("/admin/inventory?tab=counts", { replace: true });
  }, [navigate]);
  return <div className="p-8 text-center text-gray-500">جارٍ التحويل...</div>;
}

// Redirect /admin/wallet → /admin/accounting (wallet was merged into the
// unified Accounting Hub as the "محفظتي" tab to remove duplication).
function WalletToAccountingRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate("/admin/accounting?tab=wallet", { replace: true });
  }, [navigate]);
  return <div className="p-8 text-center text-gray-500">جارٍ التحويل إلى النظام المحاسبي...</div>;
}

// Redirect /admin/payment-methods-v2 → /admin/extensions?type=payment
// (payment methods were unified into the Extensions hub to remove duplication).
function PaymentMethodsV2Redirect() {
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate("/admin/extensions?type=payment", { replace: true });
  }, [navigate]);
  return <div className="p-8 text-center text-gray-500">جارٍ التحويل إلى طرق الدفع...</div>;
}

// Redirect /admin/expenses (and /admin/expenses/:id) → /admin/accounting?tab=expenses
// (expenses page was merged into the unified Accounting Hub as the "المصروفات" tab).
function ExpensesToAccountingRedirect() {
  const [, navigate] = useLocation();
  const [matched, params] = useRoute("/admin/expenses/:id");
  const focusId = matched ? params?.id : undefined;
  useEffect(() => {
    const target = focusId
      ? `/admin/accounting?tab=expenses&focus=${encodeURIComponent(focusId)}`
      : "/admin/accounting?tab=expenses";
    navigate(target, { replace: true });
  }, [navigate, focusId]);
  return <div className="p-8 text-center text-gray-500">جارٍ التحويل إلى النظام المحاسبي...</div>;
}

function PageTracker() {
  const [location] = useLocation();
  useEffect(() => {
    import("@/lib/analytics").then(m => m.trackPageView());
    import("@/lib/cart-tracking").then(m => {
      m.initCartTracking();
      m.reportPageView(location);
      if (location.startsWith("/cart")) m.reportStage("cart");
      else if (location.startsWith("/checkout")) m.reportStage("checkout");
    });
  }, [location]);
  return null;
}

const VISIT_URL = ((import.meta.env.BASE_URL || "/").replace(/\/$/, "")) + "/api/customers/visit";

function CustomerVisitTracker() {
  const [location] = useLocation();
  useEffect(() => {
    if (location.startsWith("/admin") || location.startsWith("/platform-admin")) return;
    const t = setTimeout(() => {
      const page = typeof document !== "undefined" ? (document.title || location) : location;
      const pageUrl = typeof window !== "undefined" ? window.location.href : location;
      const referrer = typeof document !== "undefined" ? document.referrer : "";
      fetch(VISIT_URL, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page: page.slice(0, 200), pageUrl, referrer }),
        keepalive: true,
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [location]);
  return null;
}

function StoreTitleSync() {
  // Keep the browser tab title in sync with the merchant's store name so
  // the page no longer shows the platform's default ("منصة نكسس كارت").
  const [location] = useLocation();
  const { data: settings } = useGetSettings();
  useEffect(() => {
    const name = (settings as any)?.storeName;
    if (!name) return;
    // Don't override pages that set their own title (product pages,
    // invoices, blog posts). Only set when current title is the default
    // platform fallback.
    const current = typeof document !== "undefined" ? document.title : "";
    const looksLikeDefault = !current
      || current === "منصة نكسس كارت"
      || current === "Nexus Cart"
      || current === name;
    if (looksLikeDefault) {
      try { document.title = name; } catch {}
    }
  }, [settings, location]);
  return null;
}

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

const HEARTBEAT_URL = ((import.meta.env.BASE_URL || "/").replace(/\/$/, "")) + "/api/visitors/heartbeat";
const HEARTBEAT_VISITOR_KEY = "nx_visitor_id";

let heartbeatVisitorIdMemo: string | null = null;

function getHeartbeatVisitorId(): string {
  if (heartbeatVisitorIdMemo) return heartbeatVisitorIdMemo;
  const gen = () => (crypto as any)?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  try {
    let id: string = localStorage.getItem(HEARTBEAT_VISITOR_KEY) || "";
    if (!id) {
      id = gen();
      try { localStorage.setItem(HEARTBEAT_VISITOR_KEY, id); } catch {}
    }
    heartbeatVisitorIdMemo = id as string;
    return id as string;
  } catch {
    try {
      let id: string = sessionStorage.getItem(HEARTBEAT_VISITOR_KEY) || "";
      if (!id) {
        id = gen();
        sessionStorage.setItem(HEARTBEAT_VISITOR_KEY, id);
      }
      heartbeatVisitorIdMemo = id as string;
      return id as string;
    } catch {
      heartbeatVisitorIdMemo = `mem-${gen()}`;
      return heartbeatVisitorIdMemo;
    }
  }
}

function getHeartbeatDevice(): { deviceType: string; os: string; browser: string } {
  const w = window.innerWidth;
  const deviceType = w < 768 ? "mobile" : w < 1024 ? "tablet" : "desktop";
  const ua = navigator.userAgent;
  let os = "Other";
  if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/Windows/.test(ua)) os = "Windows";
  else if (/Mac/.test(ua)) os = "macOS";
  else if (/Linux/.test(ua)) os = "Linux";
  let browser = "Other";
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/OPR\/|Opera/.test(ua)) browser = "Opera";
  else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = "Chrome";
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = "Safari";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  return { deviceType, os, browser };
}

function VisitorHeartbeat() {
  const [location] = useLocation();
  useEffect(() => {
    if (location.startsWith("/admin") || location.startsWith("/platform-admin")) return;
    let cancelled = false;
    let getCustomerId: () => number | null = () => null;
    import("./lib/analytics").then(m => {
      if (!cancelled) getCustomerId = m.getAnalyticsCustomerId;
    }).catch(() => {});

    const send = () => {
      try {
        const dev = getHeartbeatDevice();
        const cid = getCustomerId();
        const payload = {
          visitorId: getHeartbeatVisitorId(),
          pageUrl: window.location.pathname,
          pageTitle: document.title,
          referrer: document.referrer || "",
          deviceType: dev.deviceType,
          os: dev.os,
          browser: dev.browser,
          screenWidth: window.screen?.width,
          screenHeight: window.screen?.height,
          language: navigator.language?.split("-")[0] || "en",
          ...(cid ? { customerId: cid } : {}),
        };
        fetch(HEARTBEAT_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      } catch {}
    };
    send();
    const id = setInterval(send, 60000);
    // Re-fire immediately when the customer logs in/out so the live
    // visitors table updates without waiting up to 60s.
    const onCustomerChange = () => send();
    window.addEventListener("nx:visitor-customer-change", onCustomerChange);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("nx:visitor-customer-change", onCustomerChange);
    };
  }, [location]);
  return null;
}

// Shared tri-state host detector. Returns:
//   - "tenant"   → render the merchant store
//   - "platform" → render the platform marketing site
//   - "unknown"  → DO NOT render the platform; show a clear "domain not
//                  configured" page so unrelated domains pointing at our
//                  IP never display the wrong product.
// Module-level cache: once the hostKind is known for this tab, NEVER
// re-fetch. Previously every Gate component had its own useState +
// useEffect, so back/forward navigation between (say) /login and /
// would spawn a NEW fetch each time — and if the API was momentarily
// slow, the user saw "جار التحميل" for up to 64s per navigation. The
// host kind for a given origin cannot change without a hard reload,
// so caching it is always safe.
let __hostKindCache: "tenant" | "platform" | "unknown" | null = null;
let __hostKindPromise: Promise<"tenant" | "platform" | "unknown" | null> | null = null;
const __hostKindListeners = new Set<(k: "tenant" | "platform" | "unknown" | null) => void>();

function loadHostKind(): Promise<"tenant" | "platform" | "unknown" | null> {
  if (__hostKindCache) return Promise.resolve(__hostKindCache);
  // Canonical production platform hosts (nexuscart.co / www / app) are the
  // marketing site by definition — resolve immediately and never let a slow or
  // failing /api/_meta/host call render a store shell on the platform domain.
  if (typeof window !== "undefined" && isCanonicalPlatformHost()) {
    __hostKindCache = "platform";
    __hostKindListeners.forEach((fn) => { try { fn("platform"); } catch {} });
    return Promise.resolve("platform");
  }
  if (__hostKindPromise) return __hostKindPromise;
  const base = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
  __hostKindPromise = (async () => {
    // CRITICAL: do NOT optimistically declare a domain "unknown" when
    // _meta/host fails. The API server can be briefly slow/restarting
    // (e.g. mid-deployment, mid-schema-migration on cold start). Retry
    // with exponential backoff and only resolve when the API returns
    // an EXPLICIT hostKind. On total exhaustion return null so the
    // caller can decide (PageLoader keeps showing).
    const ATTEMPTS = 8;
    const fetchOnce = async (timeoutMs: number): Promise<unknown> => {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), timeoutMs);
      try {
        const r = await fetch(`${base}/api/_meta/host`, {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
          signal: ctrl.signal,
          cache: "no-store",
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const ct = (r.headers.get("content-type") || "").toLowerCase();
        if (!ct.includes("application/json")) throw new Error("non-json");
        return await r.json();
      } finally {
        clearTimeout(t);
      }
    };
    for (let i = 0; i < ATTEMPTS; i++) {
      try {
        const d = (await fetchOnce(8000)) as { hostKind?: string; tenant?: unknown } | null;
        const k = d?.hostKind;
        if (k === "tenant" || k === "platform" || k === "unknown") {
          __hostKindCache = k;
          __hostKindListeners.forEach((fn) => { try { fn(k); } catch {} });
          return k;
        }
        if (d && (d as any).tenant) {
          __hostKindCache = "tenant";
          __hostKindListeners.forEach((fn) => { try { fn("tenant"); } catch {} });
          return "tenant";
        }
      } catch { /* retry */ }
      const delay = Math.min(8000, 400 * 2 ** i) + Math.floor(Math.random() * 250);
      await new Promise((res) => setTimeout(res, delay));
    }
    // Exhausted retries — release the promise so a future caller can
    // retry from scratch (e.g. on next navigation / focus).
    __hostKindPromise = null;
    return null;
  })();
  return __hostKindPromise;
}

function useHostKind(): "tenant" | "platform" | "unknown" | null {
  const [kind, setKind] = useState<"tenant" | "platform" | "unknown" | null>(
    __hostKindCache ??
      (typeof window !== "undefined" && isCanonicalPlatformHost() ? "platform" : null),
  );
  useEffect(() => {
    if (__hostKindCache) { setKind(__hostKindCache); return; }
    let cancelled = false;
    const onResolved = (k: "tenant" | "platform" | "unknown" | null) => {
      if (!cancelled) setKind(k);
    };
    __hostKindListeners.add(onResolved);
    void loadHostKind().then((k) => { if (!cancelled) setKind(k); });
    return () => {
      cancelled = true;
      __hostKindListeners.delete(onResolved);
    };
  }, []);
  return kind;
}

function DomainNotConfigured() {
  // Read the host on the client so we can echo it back to the visitor.
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  return (
    <div dir="rtl" style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "#fafafa", fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      padding: 24,
    }}>
      <div style={{ maxWidth: 560, width: "100%", background: "#fff", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,.06)", padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 56, marginBottom: 8 }}>🔌</div>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, color: "#0f172a" }}>هذا النطاق غير مرتبط بأي متجر</h1>
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.8, marginBottom: 16 }}>
          النطاق <code style={{ background: "#f1f5f9", padding: "2px 6px", borderRadius: 4, direction: "ltr", display: "inline-block" }}>{host}</code> يشير إلى خوادم منصة نكسس كارت، لكنه غير مفعّل لأي متجر بعد.
        </p>
        <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.8, marginBottom: 24 }}>
          إن كنت صاحب هذا النطاق، يرجى استكمال خطوات التحقق من لوحة تحكم متجرك في صفحة "النطاق المخصص".
        </p>
        <a href="https://nexuscart.co" style={{ display: "inline-block", background: "#0ea5e9", color: "#fff", padding: "10px 22px", borderRadius: 8, textDecoration: "none", fontSize: 14, fontWeight: 500 }}>
          الذهاب إلى منصة نكسس كارت
        </a>
        <div dir="ltr" style={{ marginTop: 16, fontSize: 11, color: "#94a3b8" }}>
          This domain points at Nexus Cart but isn't linked to a store yet.
        </div>
      </div>
    </div>
  );
}

function RootGate() {
  const kind = useHostKind();
  if (!kind) return <PageLoader />;
  if (kind === "platform") return <Welcome />;
  if (kind === "unknown") return <DomainNotConfigured />;
  return <Home />;
}

function BlogIndexGate() {
  const kind = useHostKind();
  if (!kind) return <PageLoader />;
  if (kind === "platform") return <PlatformBlog />;
  if (kind === "unknown") return <DomainNotConfigured />;
  return <BlogPage />;
}

function BlogPostGate() {
  const kind = useHostKind();
  if (!kind) return <PageLoader />;
  if (kind === "platform") return <PlatformBlogPost />;
  if (kind === "unknown") return <DomainNotConfigured />;
  return <BlogPostPage />;
}

// Marketing gates: render new platform marketing UI on platform host;
// fall through to existing tenant storefront pages on tenant host.
function ContactGate() {
  const kind = useHostKind();
  if (!kind) return <PageLoader />;
  if (kind === "platform") return <MarketingContact />;
  if (kind === "unknown") return <DomainNotConfigured />;
  return <Contact />;
}
function BookMeetingGate() {
  const kind = useHostKind();
  if (!kind) return <PageLoader />;
  if (kind === "platform") return <MarketingBookMeeting />;
  if (kind === "unknown") return <DomainNotConfigured />;
  return <NotFound />;
}
function LoginGate() {
  const kind = useHostKind();
  if (!kind) return <PageLoader />;
  if (kind === "platform") return <MarketingLogin />;
  if (kind === "unknown") return <DomainNotConfigured />;
  return <Login />;
}
function RegisterGate() {
  const kind = useHostKind();
  if (!kind) return <PageLoader />;
  if (kind === "platform") return <Signup />;
  if (kind === "unknown") return <DomainNotConfigured />;
  return <Register />;
}
function FeaturesGate() {
  const kind = useHostKind();
  if (!kind) return <PageLoader />;
  if (kind === "platform") return <Features />;
  if (kind === "unknown") return <DomainNotConfigured />;
  return <NotFound />;
}
function AboutGate() {
  const kind = useHostKind();
  if (!kind) return <PageLoader />;
  if (kind === "platform") return <AboutPage />;
  if (kind === "unknown") return <DomainNotConfigured />;
  return <NotFound />;
}
// Platform marketplace page — visible only on the platform host
// (nexuscart.co). Intentionally NOT linked from MarketingNavbar/Footer yet
// (per user request, pending review). Direct URL: /apps
function AppsGate() {
  const kind = useHostKind();
  if (!kind) return <PageLoader />;
  if (kind === "platform") return <PlatformApps />;
  if (kind === "unknown") return <DomainNotConfigured />;
  return <NotFound />;
}
function NotFoundGate() {
  const kind = useHostKind();
  if (!kind) return <PageLoader />;
  if (kind === "platform") return <MarketingNotFound />;
  if (kind === "unknown") return <DomainNotConfigured />;
  return <NotFound />;
}
function LegalGate({ children }: { children: React.ReactNode }) {
  const kind = useHostKind();
  if (!kind) return <PageLoader />;
  // On the platform marketing site (nexuscart.co) there is no tenant, so
  // StoreLayout would crash — wrap legal pages in MarketingShell instead
  // so they still get the marketing header + footer.
  if (kind === "platform" || kind === "unknown") return <MarketingShell>{children}</MarketingShell>;
  // Tenant host: the persistent StoreLayout is hoisted ABOVE the Switch
  // by CurrentLayout, so we return children directly. Wrapping here again
  // would mount a second StoreLayout and reintroduce the navigation flash.
  return <>{children}</>;
}

// Persistent layout selector — hoisted ABOVE the <Switch> so the
// storefront chrome (StoreLayout: navbar, mega-menu, footer, promo strip,
// notification bars) stays mounted across navigations within the tenant
// host. Before this, every Route inlined its own <StoreLayout> wrapper,
// so a Switch swap unmounted+remounted the entire chrome on every link
// click — users on luxurygive.com perceived this as the page "flashing"
// or "going blank then coming back" even after the View Transitions
// layer was added. The four prior flash-prevention layers (PageLoader
// grace, useDeferredValue, idle preloaders, View Transitions) all
// helped, but the only true fix for the perceived flicker was to stop
// remounting the layout itself. By picking the layout once based on
// location + hostKind, React reconciles the same element type across
// route changes (e.g. /cart → /shop both yield <StoreLayout>) and keeps
// it mounted, swapping only the children area. Admin paths intentionally
// continue to wrap themselves with <AdminLayout> per-route (separate
// refactor) — CurrentLayout returns a fragment for them so behavior is
// unchanged. Platform-host marketing routes also fall through unchanged
// because the marketing Gates render their own MarketingShell.
const STANDALONE_PREFIXES = [
  "/admin/login",
  "/admin/upgrade",
  "/admin/forgot-password",
  "/admin/reset-password",
  "/admin/verify-email",
  "/admin/force-change-password",
  "/admin/oauth/consent",
  "/supplier",
  "/logistics",
  "/account/privacy",
  "/account/quotes",
  "/welcome",
  "/invoice/",
  "/credit-note/",
  "/shipping-label/",
  "/packing-list/",
  "/print/",
  "/nexus-store",
];
function matchesStandalone(loc: string): boolean {
  for (const p of STANDALONE_PREFIXES) {
    if (p.endsWith("/")) {
      if (loc.startsWith(p)) return true;
    } else {
      if (loc === p || loc.startsWith(p + "/")) return true;
    }
  }
  return false;
}
function CurrentLayout({
  children,
  hostKind,
  location,
}: {
  children: ReactNode;
  hostKind: "tenant" | "platform" | "unknown" | null;
  location: string;
}) {
  // Standalone (printable invoices, supplier portal, account-privacy DSAR,
  // admin auth pages, etc.) — no shared chrome.
  if (matchesStandalone(location)) return <>{children}</>;
  // Admin: keep existing per-route <AdminLayout> wrappers (out of scope
  // for the storefront flash fix). Returning a fragment here means
  // CurrentLayout is a no-op on admin paths and admin behavior is
  // bit-identical to before.
  if (location.startsWith("/admin")) return <>{children}</>;
  // Platform-admin (super-admin) panel — never wrap in storefront chrome.
  // These pages render their own <PlatformLayout>. Without this explicit
  // guard, /platform-admin relies SOLELY on hostKind === "platform" to
  // avoid StoreLayout; if hostKind mis-resolves to "tenant" (e.g. a stale
  // X-Tenant-Slug left over from impersonation taints /api/_meta/host),
  // the tenant storefront header gets "stuck" on top of the panel.
  if (location.startsWith("/platform-admin")) return <>{children}</>;
  // Platform marketing host — the per-page MarketingGate components
  // (ContactGate, FeaturesGate, BlogIndexGate, BookMeetingGate, ...)
  // render their own MarketingShell. We must not double-wrap. Also
  // applies to "unknown" host (DomainNotConfigured handles its own UI).
  if (hostKind === "platform" || hostKind === "unknown") return <>{children}</>;
  // Tenant host storefront — persistent layout. We render StoreLayout
  // even while hostKind is still being resolved (null) because the vast
  // majority of traffic IS tenant, and crucially: if we render a
  // fragment while loading and then swap to StoreLayout once hostKind
  // resolves, React tears down the entire child subtree and remounts it
  // inside the new wrapper — that triggers a full re-fetch of every
  // page's data, resets scroll position, and on slow API responses
  // leaves the user staring at a blank page (the May 2026 "stuck on
  // loading" report). For the small minority of platform-host loads,
  // the platform gates short-circuit BEFORE rendering tenant pages
  // (each gate returns its own Marketing* component when kind ===
  // "platform"), so this default never leaks tenant chrome onto the
  // platform host.
  return <StoreLayout>{children}</StoreLayout>;
}

// Tiny, intentionally-invisible Suspense fallback used inside the
// persistent CurrentLayout. The four prior flash-prevention layers
// (PageLoader grace, useDeferredValue, idle preloaders, View Transitions)
// almost always keep the OLD route painted while the new chunk loads, so
// this fallback is purely defensive — it only paints if a route's lazy
// chunk genuinely fails to be cached and the network is slow. We render
// an empty <div> sized to the typical content height so the layout
// doesn't collapse and trigger a CLS reflow underneath the persistent
// header/footer; we don't show a spinner because the header is already
// visible and that's the "instant feedback" the user wants on click.
function ContentSlotFallback() {
  return <div aria-hidden="true" style={{ minHeight: "60vh" }} />;
}

function Router() {
  const hostKind = useHostKind();
  // React 18 concurrent trick: keep rendering the PREVIOUS route while
  // the next lazy chunk is loading. Without this, every link click
  // unmounted the current page and replaced it with the <Suspense>
  // fallback (blank/loader) for 80–400 ms — users perceived the entire
  // store as "disappearing then coming back" on every navigation
  // (luxurygive.com, May 2026). useDeferredValue lets React paint the
  // old <Switch location={...}> result until the new one's chunks
  // resolve, then swap atomically. No spinner, no blank flash.
  const [location] = useLocation();
  const deferredLocation = useDeferredValue(location);
  return (
    <>
      <PlatformSeoManager enabled={hostKind === "platform"} />
    <CurrentLayout hostKind={hostKind} location={deferredLocation}>
    <Suspense fallback={<ContentSlotFallback />}>
    <Switch location={deferredLocation}>
      {/* Legacy image redirect.
          Old OpenCart-era URLs of the form /legacy/catalog/<path>/<file>.jpg
          (which used to live on luxuryav.net) still circulate in Google's
          index, blog backlinks, and shared screenshots. The SPA otherwise
          renders the 404 page when these are visited directly.
          We catch the path and bounce the request through /img-proxy so
          the original image at luxuryav.net is fetched, optimised, and
          returned as image bytes — preserving SEO equity and existing
          deep links. */}
      <Route path="/legacy/catalog/:rest*" component={(props: any) => {
        const rest = (props?.params?.rest as string | undefined) || "";
        const target = `https://luxuryav.net/image/catalog/${rest}`;
        const proxied = `${import.meta.env.BASE_URL}api/img-proxy?url=${encodeURIComponent(target)}`;
        if (typeof window !== "undefined") window.location.replace(proxied);
        return null;
      }} />

      {/* Admin Login (no layout) */}
      <Route path="/admin/login" component={() => <AdminLogin />} />
      <Route path="/admin/upgrade" component={() => <AdminUpgrade />} />
      <Route path="/admin/forgot-password" component={() => <AdminForgotPassword />} />
      <Route path="/admin/reset-password" component={() => <AdminResetPassword />} />
      <Route path="/admin/verify-email" component={() => <AdminVerifyEmail />} />
      <Route path="/admin/force-change-password" component={() => <AdminForceChangePassword />} />

      {/* Admin Routes */}
      <Route path="/admin" component={() => <AdminLayout><AdminDashboard /></AdminLayout>} />
      {/* Nexus Academy — merchant training, rendered inside the dashboard
          chrome only. Public certificate verification stays on the
          storefront (see /academy/certificates/:uuid below). */}
      <Route path="/admin/academy" component={() => <AdminLayout><AcademyIndex /></AdminLayout>} />
      <Route path="/admin/academy/:slug/:lessonSlug" component={() => <AdminLayout><AcademyLesson /></AdminLayout>} />
      <Route path="/admin/academy/:slug" component={() => <AdminLayout><AcademyCourse /></AdminLayout>} />
      <Route path="/admin/products" component={() => <AdminLayout><AdminProducts /></AdminLayout>} />
      <Route path="/admin/products/bulk" component={() => <AdminLayout><AdminProductsBulk /></AdminLayout>} />
      <Route path="/admin/import-from-competitor" component={() => <AdminLayout><AdminImportFromCompetitor /></AdminLayout>} />
      <Route path="/admin/integrations" component={() => <AdminLayout><AdminIntegrations /></AdminLayout>} />
      <Route path="/admin/connect-hub" component={() => <AdminLayout><AdminConnectHub /></AdminLayout>} />
      <Route path="/admin/connect-hub/webhooks" component={() => <AdminLayout><AdminConnectHubWebhooks /></AdminLayout>} />
      <Route path="/admin/connect-hub/api-keys" component={() => <AdminLayout><AdminConnectHubApiKeys /></AdminLayout>} />
      <Route path="/admin/corporate-requests" component={() => <AdminLayout><AdminCorporateRequests /></AdminLayout>} />
      <Route path="/admin/corporate-gifts-settings" component={() => <AdminLayout><AdminCorporateGiftsSettings /></AdminLayout>} />
      <Route path="/admin/hr-leaves" component={() => <AdminLayout><AdminHrLeaves /></AdminLayout>} />
      <Route path="/corporate-gifts" component={() => <CorporateGifts />} />
      <Route path="/admin/nexus-store" component={() => <AdminLayout><AdminNexusStore /></AdminLayout>} />
      <Route path="/nexus-store" component={() => <NexusCartProvider><NexusStoreLanding /></NexusCartProvider>} />
      <Route path="/nexus-store/browse" component={() => <NexusCartProvider><NexusStoreBrowse /></NexusCartProvider>} />
      <Route path="/nexus-store/categories" component={() => <NexusCartProvider><NexusStoreCategories /></NexusCartProvider>} />
      <Route path="/nexus-store/blog" component={() => <NexusCartProvider><NexusStoreBlog /></NexusCartProvider>} />
      <Route path="/nexus-store/cart" component={() => <NexusCartProvider><NexusStoreCart /></NexusCartProvider>} />
      <Route path="/nexus-store/checkout" component={() => <NexusCartProvider><NexusStoreCheckout /></NexusCartProvider>} />
      <Route path="/nexus-store/p/:tenantId/:productId" component={() => <NexusCartProvider><NexusStoreProduct /></NexusCartProvider>} />
      <Route path="/nexus-store/m/:slug" component={() => <NexusCartProvider><NexusStoreMerchant /></NexusCartProvider>} />
      <Route path="/admin/products/new" component={() => <AdminLayout><AdminProductEdit /></AdminLayout>} />
      <Route path="/admin/products/:id/edit" component={() => <AdminLayout><AdminProductEdit /></AdminLayout>} />
      <Route path="/admin/product-options" component={() => <AdminLayout><AdminProductOptionsCatalog /></AdminLayout>} />
      <Route path="/admin/categories" component={() => <AdminLayout><AdminCategories /></AdminLayout>} />
      <Route path="/admin/tags" component={() => <AdminLayout><AdminTags /></AdminLayout>} />
      <Route path="/tag/:slug" component={() => <TagPage />} />
      <Route path="/admin/categories/new" component={() => <AdminLayout><AdminCategoryEdit /></AdminLayout>} />
      <Route path="/admin/categories/:id/edit" component={() => <AdminLayout><AdminCategoryEdit /></AdminLayout>} />
      <Route path="/admin/orders" component={() => <AdminLayout><AdminOrders /></AdminLayout>} />
      <Route path="/admin/orders/:id" component={() => <AdminLayout><AdminOrderDetail /></AdminLayout>} />
      <Route path="/admin/customers" component={() => <AdminLayout><AdminCustomers /></AdminLayout>} />
      <Route path="/admin/customers/:id" component={() => <AdminLayout><AdminCustomerDetail /></AdminLayout>} />
      <Route path="/admin/coupons" component={() => <AdminLayout><AdminCoupons /></AdminLayout>} />
      <Route path="/admin/banners" component={() => <AdminLayout><AdminBanners /></AdminLayout>} />
      <Route path="/admin/store-features" component={() => <AdminLayout><AdminStoreFeatures /></AdminLayout>} />
      {/* Alias: links from appearance / homepage-layout point to /admin/trust-badges,
          but the actual page (which manages both trust badges and store stats)
          lives at /admin/store-features. Keep the alias so the merchant-facing
          URL doesn't 404. */}
      <Route path="/admin/trust-badges" component={() => <AdminLayout><AdminStoreFeatures /></AdminLayout>} />
      <Route path="/admin/returns" component={() => <AdminLayout><AdminReturns /></AdminLayout>} />
      <Route path="/admin/multi-offers" component={() => <AdminLayout><AdminMultiOffers /></AdminLayout>} />
      <Route path="/admin/auctions" component={() => <RequireFeature slug="auctions"><AdminLayout><AdminAuctions /></AdminLayout></RequireFeature>} />
      <Route path="/admin/new-arrivals" component={() => <AdminLayout><AdminNewArrivals /></AdminLayout>} />
      <Route path="/admin/brands" component={() => <AdminLayout><AdminBrands /></AdminLayout>} />
      <Route path="/admin/size-guides" component={() => <AdminLayout><AdminSizeGuides /></AdminLayout>} />
      <Route path="/admin/suppliers" component={() => <AdminLayout><AdminSuppliers /></AdminLayout>} />
      <Route path="/admin/suppliers/:id" component={() => <AdminLayout><AdminSupplierDetail /></AdminLayout>} />
      <Route path="/admin/mto-orders" component={() => <AdminLayout><AdminMtoOrders /></AdminLayout>} />
      <Route path="/supplier/login" component={() => <SupplierLogin />} />
      <Route path="/supplier/register" component={() => <SupplierRegister />} />
      <Route path="/supplier" component={() => <SupplierPortal />} />
      <Route path="/logistics/login" component={() => <LogisticsLogin />} />
      <Route path="/logistics" component={() => <LogisticsPortal />} />
      <Route path="/admin/logistics" component={() => <AdminLayout><LogisticsDashboard /></AdminLayout>} />
      <Route path="/admin/logistics-users" component={() => <AdminLayout><LogisticsUsersAdmin /></AdminLayout>} />
      <Route path="/admin/purchase-orders" component={() => <AdminLayout><PurchaseOrdersAdmin /></AdminLayout>} />
      <Route path="/admin/purchase-orders/:id" component={() => <AdminLayout><PurchaseOrdersAdmin /></AdminLayout>} />
      <Route path="/admin/deliveries" component={() => <AdminLayout><DeliveriesAdmin /></AdminLayout>} />
      <Route path="/admin/expenses" component={ExpensesToAccountingRedirect} />
      <Route path="/admin/expenses/:id" component={ExpensesToAccountingRedirect} />
      <Route path="/admin/expense-categories" component={() => <AdminLayout><ExpenseCategoriesAdmin /></AdminLayout>} />
      <Route path="/admin/accounting" component={() => <RequireFeature slug="accounting"><AdminLayout><AccountingHub /></AdminLayout></RequireFeature>} />
      <Route path="/admin/settings" component={() => <AdminLayout><AdminSettings /></AdminLayout>} />
      <Route path="/admin/settings/auth" component={() => <AdminLayout><AdminAuthSettings /></AdminLayout>} />
      <Route path="/admin/dashboard-settings" component={() => <AdminLayout><AdminDashboardSettings /></AdminLayout>} />
      <Route path="/admin/custom-domain" component={() => <RequireFeature slug="custom_domain"><AdminLayout><AdminCustomDomain /></AdminLayout></RequireFeature>} />
      <Route path="/admin/data-privacy" component={() => <AdminLayout><AdminDataPrivacy /></AdminLayout>} />
      <Route path="/account/privacy" component={AccountPrivacy} />
      <Route path="/account/quotes" component={AccountQuotes} />
      <Route path="/admin/reviews" component={() => <AdminLayout><AdminReviews /></AdminLayout>} />
      <Route path="/admin/pages" component={() => <AdminLayout><AdminInfoPages /></AdminLayout>} />
      <Route path="/admin/apps" component={() => <AdminLayout><AdminAppsMarketplace /></AdminLayout>} />
      <Route path="/admin/apps/installed" component={() => <AdminLayout><AdminApps /></AdminLayout>} />
      <Route path="/admin/apps/:appId" component={() => <AdminLayout><AdminAppDetail /></AdminLayout>} />
      {/* Merchant-facing app store / integrations catalog. Intentionally
          NOT yet added to AdminLayout sidebar (per user request, pending
          review). Direct URL: /admin/app-store */}
      <Route path="/admin/app-store" component={() => <AdminLayout><AdminAppStore /></AdminLayout>} />
      <Route path="/admin/personal-tokens" component={() => <AdminLayout><AdminPersonalTokens /></AdminLayout>} />
      <Route path="/admin/oauth/consent" component={AdminOAuthConsent} />
      <Route path="/admin/analytics" component={() => <AdminLayout><AdminAnalytics /></AdminLayout>} />
      <Route path="/admin/google-analytics" component={() => <AdminLayout><AdminGoogleAnalytics /></AdminLayout>} />
      <Route path="/admin/visitor-log" component={() => <AdminLayout><AdminVisitorLog /></AdminLayout>} />
      <Route path="/admin/reports" component={() => <AdminLayout><AdminReports /></AdminLayout>} />
      <Route path="/admin/reports/sales" component={() => <AdminLayout><ReportsSales /></AdminLayout>} />
      <Route path="/admin/reports/products" component={() => <AdminLayout><ReportsProducts /></AdminLayout>} />
      <Route path="/admin/reports/customers" component={() => <AdminLayout><ReportsCustomers /></AdminLayout>} />
      <Route path="/admin/reports/marketing" component={() => <AdminLayout><ReportsMarketing /></AdminLayout>} />
      <Route path="/admin/reports/orders" component={() => <AdminLayout><ReportsOrders /></AdminLayout>} />
      <Route path="/admin/reports/returns" component={() => <AdminLayout><ReportsReturns /></AdminLayout>} />
      <Route path="/admin/extensions" component={() => <AdminLayout><AdminExtensions /></AdminLayout>} />
      <Route path="/admin/extensions/:id" component={() => <AdminLayout><ExtensionEdit /></AdminLayout>} />
      <Route path="/admin/file-manager" component={() => <AdminLayout><AdminFileManager /></AdminLayout>} />
      <Route path="/admin/notifications" component={() => <AdminLayout><AdminNotificationsPage /></AdminLayout>} />
      <Route path="/admin/notification-settings" component={() => <AdminLayout><AdminNotificationSettingsPage /></AdminLayout>} />
      <Route path="/admin/customer-notification-settings" component={() => <AdminLayout><AdminCustomerNotificationSettingsPage /></AdminLayout>} />
      <Route path="/admin/sender-domains" component={() => <AdminLayout><AdminSenderDomainsPage /></AdminLayout>} />
      <Route path="/admin/messaging-providers" component={() => <AdminLayout><AdminMessagingProvidersPage /></AdminLayout>} />
      <Route path="/admin/system-performance" component={SystemPerformanceTenantRedirect} />
      <Route path="/admin/system-monitoring" component={SystemMonitoringTenantRedirect} />
      <Route path="/admin/scheduled-jobs" component={ScheduledJobsTenantRedirect} />
      <Route path="/admin/custom-css" component={() => <RequireFeature slug="custom_css"><AdminLayout><AdminCustomCss /></AdminLayout></RequireFeature>} />
      <Route path="/admin/conversion-tracking" component={() => <AdminLayout><AdminConversionTracking /></AdminLayout>} />
      <Route path="/admin/capi-logs" component={() => <AdminLayout><AdminCapiLogs /></AdminLayout>} />
      <Route path="/admin/security-2fa" component={() => <AdminLayout><AdminSecurity2FA /></AdminLayout>} />
      <Route path="/admin/trash" component={TrashRedirect} />
      <Route path="/admin/stock-ledger" component={StockLedgerToInventoryRedirect} />
      <Route path="/admin/price-history" component={() => <AdminLayout><AdminPriceHistory /></AdminLayout>} />
      <Route path="/admin/bulk-actions" component={() => <AdminLayout><AdminBulkActions /></AdminLayout>} />
      <Route path="/admin/gift-vouchers" component={() => <RequireFeature slug="gift_vouchers"><AdminLayout><AdminGiftVouchers /></AdminLayout></RequireFeature>} />
      <Route path="/admin/shipments" component={() => <AdminLayout><AdminShipments /></AdminLayout>} />
      <Route path="/admin/customer-groups" component={() => <AdminLayout><AdminCustomerGroups /></AdminLayout>} />
      <Route path="/admin/customer-fields" component={() => <AdminLayout><AdminCustomerFields /></AdminLayout>} />
      <Route path="/admin/google-shopping" component={() => <VisibleErrorBoundary><AdminLayout><AdminGoogleShopping /></AdminLayout></VisibleErrorBoundary>} />
      <Route path="/admin/facebook-catalog" component={() => <AdminLayout><AdminFacebookCatalog /></AdminLayout>} />
      <Route path="/admin/snapchat-catalog" component={() => <AdminLayout><AdminSnapchatCatalog /></AdminLayout>} />
      <Route path="/admin/tiktok-catalog" component={() => <AdminLayout><AdminTiktokCatalog /></AdminLayout>} />
      <Route path="/admin/affiliates" component={() => <RequireFeature slug="affiliates"><AdminLayout><AdminAffiliates /></AdminLayout></RequireFeature>} />
      <Route path="/admin/email-campaigns" component={() => <RequireFeature slug="email_campaigns"><AdminLayout><AdminEmailCampaigns /></AdminLayout></RequireFeature>} />
      <Route path="/admin/sms-campaigns" component={() => <RequireFeature slug="sms_campaigns"><AdminLayout><AdminSmsCampaigns /></AdminLayout></RequireFeature>} />
      <Route path="/admin/blog-settings" component={() => <AdminLayout><AdminBlogSettings /></AdminLayout>} />
      <Route path="/admin/blog-categories" component={() => <AdminLayout><AdminBlogCategories /></AdminLayout>} />
      <Route path="/admin/blog-posts" component={() => <RequireFeature slug="blog"><AdminLayout><AdminBlogPosts /></AdminLayout></RequireFeature>} />
      <Route path="/admin/blog-comments" component={() => <AdminLayout><AdminBlogComments /></AdminLayout>} />
      <Route path="/admin/contact-messages" component={() => <AdminLayout><AdminContactMessages /></AdminLayout>} />
      <Route path="/admin/seo" component={() => <AdminLayout><AdminSeo /></AdminLayout>} />
      <Route path="/admin/appearance" component={() => <AdminLayout><AdminAppearance /></AdminLayout>} />
      <Route path="/admin/themes" component={() => <AdminLayout><AdminThemes /></AdminLayout>} />
      <Route path="/admin/themes/library" component={() => <AdminLayout><AdminThemes initialTab="library" /></AdminLayout>} />
      <Route path="/admin/themes/purchases" component={() => <AdminLayout><AdminThemesPurchases /></AdminLayout>} />
      <Route path="/admin/themes/:slug/customize" component={() => <AdminLayout><AdminThemeCustomize /></AdminLayout>} />
      <Route path="/admin/themes/:slug" component={() => <AdminLayout><AdminThemePreview /></AdminLayout>} />
      <Route path="/admin/mobile-menu" component={() => <AdminLayout><AdminMobileMenu /></AdminLayout>} />
      <Route path="/admin/journal" component={() => { window.location.replace("/admin/appearance"); return null; }} />
      <Route path="/admin/abandoned-carts" component={() => <RequireFeature slug="abandoned_carts"><AdminLayout><AdminAbandonedCarts /></AdminLayout></RequireFeature>} />
      <Route path="/admin/whatsapp-ai" component={() => <AdminLayout><AdminWhatsAppAi /></AdminLayout>} />
      <Route path="/admin/site-chat" component={() => <RequireFeature slug="site_chatbot"><AdminLayout><AdminSiteChat /></AdminLayout></RequireFeature>} />
      <Route path="/admin/whatsapp-campaigns" component={() => <AdminLayout><AdminWhatsAppCampaigns /></AdminLayout>} />
      <Route path="/admin/activity-log" component={() => <AdminLayout><AdminActivityLog /></AdminLayout>} />
      <Route path="/admin/marketing-tracking" component={() => <AdminLayout><AdminMarketingTracking /></AdminLayout>} />
      <Route path="/admin/layouts" component={() => <AdminLayout><AdminLayouts /></AdminLayout>} />
      <Route path="/admin/homepage-layout" component={() => <AdminLayout><AdminHomepageLayout /></AdminLayout>} />
      <Route path="/admin/page-layouts" component={() => <AdminLayout><AdminPageLayoutsHub /></AdminLayout>} />
      <Route path="/admin/page-layouts/:pageKey/blocks" component={() => <AdminLayout><AdminHomepageLayout /></AdminLayout>} />
      <Route path="/admin/page-layouts/:pageId" component={() => <AdminLayout><AdminPageLayoutEditor /></AdminLayout>} />
      <Route path="/admin/custom-pages" component={() => <AdminLayout><AdminCustomPages /></AdminLayout>} />
      <Route path="/admin/custom-pages/:id" component={() => <AdminLayout><AdminHomepageLayout /></AdminLayout>} />
      <Route path="/p/:slug" component={() => <CustomPageView />} />
      <Route path="/admin/modules" component={() => <AdminLayout><AdminModules /></AdminLayout>} />
      <Route path="/admin/product-extras" component={() => <AdminLayout><AdminProductExtras /></AdminLayout>} />
      <Route path="/admin/warehouses" component={() => <RequireFeature slug="multi_warehouse"><AdminLayout><AdminWarehouses /></AdminLayout></RequireFeature>} />
      <Route path="/admin/companies" component={() => <AdminLayout><AdminCompanies /></AdminLayout>} />
      <Route path="/admin/quotes" component={() => <AdminLayout><AdminQuotes /></AdminLayout>} />
      <Route path="/admin/content-staging" component={() => <AdminLayout><AdminContentStaging /></AdminLayout>} />
      <Route path="/admin/pricing-rules" component={() => <AdminLayout><AdminPricingRules /></AdminLayout>} />
      <Route path="/admin/ai-search" component={() => <RequireFeature slug="ai_search"><AdminLayout><AdminAISearch /></AdminLayout></RequireFeature>} />
      <Route path="/admin/packaging" component={() => <AdminLayout><AdminPackaging /></AdminLayout>} />
      <Route path="/admin/inventory" component={() => <AdminInventory />} />
      {/*
        Inventory counts now lives as a tab inside /admin/inventory.
        The standalone /admin/inventory-counts URL is preserved for
        bookmarks but immediately redirects to the unified location
        so the same feature is not exposed in two different places.
        The per-session URL /:id is unchanged because the scan-based
        session view is intentionally a full-screen workflow.
      */}
      <Route path="/admin/inventory-counts" component={InventoryCountsListRedirect} />
      <Route path="/admin/inventory-counts/:id" component={() => <AdminLayout><AdminInventoryCountSession /></AdminLayout>} />
      <Route path="/admin/loyalty" component={() => <RequireFeature slug="loyalty"><AdminLoyalty /></RequireFeature>} />
      <Route path="/admin/loyalty-hub" component={() => <AdminLayout><AdminLoyaltyHub /></AdminLayout>} />
      <Route path="/admin/pos" component={() => <AdminLayout><AdminPos /></AdminLayout>} />
      <Route path="/admin/bi" component={() => <RequireFeature slug="bi"><AdminLayout><AdminBi /></AdminLayout></RequireFeature>} />
      <Route path="/admin/ai-studio" component={() => <RequireFeature slug="ai_studio"><AdminLayout><AdminAiStudio /></AdminLayout></RequireFeature>} />
      <Route path="/admin/product-qa" component={() => <AdminProductQA />} />
      <Route path="/admin/bundles" component={() => <AdminBundles />} />
      <Route path="/admin/companion-lists" component={() => <AdminCompanionLists />} />
      <Route path="/admin/popup-settings" component={() => <AdminLayout><AdminPopupSettings /></AdminLayout>} />
      <Route path="/admin/whatsapp-settings" component={() => <AdminLayout><AdminWhatsAppSettings /></AdminLayout>} />
      <Route path="/admin/dark-mode-settings" component={() => <AdminLayout><AdminDarkModeSettings /></AdminLayout>} />
      <Route path="/admin/invoice-settings" component={() => <AdminLayout><AdminInvoiceSettings /></AdminLayout>} />
      <Route path="/admin/tax-system" component={() => <AdminTaxSystem />} />
      <Route path="/admin/tax-compliance" component={() => <AdminLayout><AdminTaxCompliance /></AdminLayout>} />
      <Route path="/admin/trust" component={() => <AdminLayout><AdminTrust /></AdminLayout>} />
      <Route path="/admin/zatca-onboarding" component={() => <AdminLayout><AdminZatcaOnboarding /></AdminLayout>} />
      <Route path="/admin/admin-groups" component={() => <AdminAdminGroups />} />
      <Route path="/admin/attributes" component={() => <AdminAttributes />} />
      <Route path="/admin/sidebar-filters" component={() => <AdminLayout><AdminSidebarFilters /></AdminLayout>} />
      <Route path="/admin/filter-groups" component={() => <AdminLayout><AdminFilterGroups /></AdminLayout>} />
      <Route path="/admin/filter-groups/new" component={() => <AdminLayout><AdminFilterGroupEdit /></AdminLayout>} />
      <Route path="/admin/filter-groups/:id/edit" component={() => <AdminLayout><AdminFilterGroupEdit /></AdminLayout>} />
      <Route path="/admin/email-templates" component={() => <AdminEmailTemplates />} />
      <Route path="/admin/return-config" component={() => <AdminReturnConfig />} />
      <Route path="/admin/order-settings" component={() => <AdminLayout><AdminOrderSettings /></AdminLayout>} />
      <Route path="/admin/anti-fraud" component={() => <RequireFeature slug="anti_fraud"><AdminAntiFraud /></RequireFeature>} />
      <Route path="/admin/gdpr" component={GdprToDataPrivacyRedirect} />
      <Route path="/admin/error-logs" component={ErrorLogsTenantRedirect} />
      <Route path="/admin/db-backup" component={() => <AdminDbBackup />} />
      <Route path="/admin/subscriptions" component={() => <RequireFeature slug="subscriptions"><AdminSubscriptions /></RequireFeature>} />
      <Route path="/admin/stock-statuses" component={() => <AdminStockStatuses />} />
      <Route path="/admin/order-totals" component={() => <AdminOrderTotals />} />
      <Route path="/admin/stores" component={() => <AdminStores />} />
      <Route path="/admin/linked-stores" component={() => <AdminLayout><AdminLinkedStores /></AdminLayout>} />
      <Route path="/admin/mail-queue" component={() => <AdminMailQueue />} />
      <Route path="/admin/messaging-logs" component={() => <AdminMessagingLogs />} />
      <Route path="/admin/messaging-optouts" component={() => <AdminMessagingOptouts />} />
      <Route path="/admin/event-hooks" component={() => <AdminEventHooks />} />
      <Route path="/admin/languages" component={() => <AdminLanguages />} />
      <Route path="/admin/marketplace" component={() => <AdminMarketplace />} />
      <Route path="/admin/countries" component={CountriesTenantRedirect} />
      <Route path="/platform-admin/countries" component={PlatformCountries} />
      <Route path="/admin/order-statuses" component={() => <AdminOrderStatuses />} />
      <Route path="/admin/weight-length-classes" component={WeightLengthClassesTenantRedirect} />
      <Route path="/platform-admin/weight-length-classes" component={PlatformWeightLengthClasses} />
      <Route path="/admin/grouped-products" component={() => <AdminGroupedProducts />} />
      <Route path="/admin/customer-segments" component={() => <AdminCustomerSegments />} />
      <Route path="/admin/related-rules" component={() => <AdminRelatedRules />} />
      <Route path="/admin/product-alerts" component={() => <AdminProductAlerts />} />
      <Route path="/admin/comparison-report" component={() => <AdminComparisonReport />} />
      <Route path="/admin/import-export" component={() => <AdminLayout><AdminExportImportCenter /></AdminLayout>} />
      <Route path="/admin/export-import" component={() => <AdminLayout><AdminExportImportCenter /></AdminLayout>} />
      <Route path="/admin/url-rewrites" component={() => <AdminUrlRewrites />} />
      <Route path="/admin/cache-management" component={() => <AdminCacheManagement />} />
      <Route path="/admin/indexer" component={() => <AdminIndexer />} />
      <Route path="/admin/flash-sales" component={() => <RequireFeature slug="flash_sales"><AdminLayout><AdminFlashSales /></AdminLayout></RequireFeature>} />
      <Route path="/admin/bundle-offers" component={() => <AdminLayout><AdminBundleOffers /></AdminLayout>} />
      <Route path="/admin/auto-reviews" component={() => <AdminLayout><AdminAutoReviews /></AdminLayout>} />
      <Route path="/admin/referral-program" component={() => <AdminLayout><AdminReferralProgram /></AdminLayout>} />
      <Route path="/admin/push-notifications" component={() => <AdminLayout><AdminPushNotifications /></AdminLayout>} />
      <Route path="/admin/wishlist-analytics" component={() => <AdminLayout><AdminWishlistAnalytics /></AdminLayout>} />
      <Route path="/admin/campaign-roi" component={() => <AdminLayout><AdminCampaignROI /></AdminLayout>} />

      {/* Auth Routes (host-gated: marketing on platform, customer auth on tenant) */}
      <Route path="/login" component={LoginGate} />
      <Route path="/register" component={RegisterGate} />
      <Route path="/account" component={() => <Account />} />
      <Route path="/wishlist" component={() => <WishlistPage />} />
      <Route path="/compare" component={() => <ComparePage />} />

      {/* Storefront Routes */}
      <Route path="/welcome" component={() => <Welcome />} />
      <Route path="/" component={() => <RootGate />} />
      <Route path="/new-arrivals" component={() => <NewArrivalsPage />} />
      <Route path="/auctions" component={() => <AuctionsPage />} />
      <Route path="/offers/:slug" component={() => <OfferPage />} />
      <Route path="/shop" component={() => <Shop />} />
      <Route path="/category/:id" component={(props: any) => <CategoryRedirect id={props.params?.id} />} />
      <Route path="/product/:id" component={(props: any) => (
        // The `key` forces React to fully unmount and remount ProductDetail
        // when the URL id changes. Without it, in-component state (selected
        // gallery image, quantity, selectedOptions, companion-product
        // picks, etc.) and React Query's `placeholderData` carry over from
        // the previous product — causing the page to look "frozen" with
        // stale content until the user hard-refreshes.
        <ProductDetail key={props.params?.id} />
      )} />
      <Route path="/cart" component={() => <CartPage />} />
      <Route path="/checkout" component={() => <Checkout />} />
      <Route path="/order-confirmation/:id" component={() => <OrderConfirmation />} />
      <Route path="/payment/hyperpay/:orderId" component={() => <PaymentHyperpay />} />
      <Route path="/payment/auction-deposit/:auctionId" component={() => <PaymentAuctionDeposit />} />
      <Route path="/payment/auction-winner/:auctionId" component={() => <PaymentAuctionWinner />} />
      <Route path="/payment/paylink/:orderId" component={() => <PaymentPaylink />} />
      <Route path="/payment/neoleap/:orderId" component={() => <PaymentNeoleap />} />
      <Route path="/payment/tamara/:orderId" component={() => <PaymentTamara />} />
      <Route path="/payment/tabby/:orderId" component={() => <PaymentTabby />} />
      <Route path="/payment/stc-pay/:orderId" component={() => <PaymentStcPay />} />
      <Route path="/payment/payfort/:orderId" component={() => <PaymentPayfort />} />
      <Route path="/split-pay/:token" component={() => <SplitPay />} />
      <Route path="/split-payment/:orderId" component={() => <SplitPaymentTracker />} />
      <Route path="/contact" component={ContactGate} />
      <Route path="/features" component={FeaturesGate} />
      <Route path="/about" component={AboutGate} />
      <Route path="/apps" component={AppsGate} />
      <Route path="/order-tracking" component={() => <OrderTracking />} />
      <Route path="/brands" component={() => <BrandsPage />} />
      <Route path="/categories" component={() => <CategoriesPage />} />
      <Route path="/faq" component={() => <LegalGate><FAQPage /></LegalGate>} />
      <Route path="/gift-certificates" component={() => <GiftCertificates />} />
      <Route path="/affiliate-program" component={() => <AffiliateProgram />} />
      <Route path="/influencers" component={() => <InfluencersPage />} />
      <Route path="/referral" component={() => <ReferralPage />} />
      <Route path="/sitemap" component={() => <SitemapPage />} />
      <Route path="/blog/:slug" component={BlogPostGate} />
      <Route path="/blog" component={BlogIndexGate} />
      {/* Academy is a merchant-only dashboard feature now (see /admin/academy
          routes). Old public storefront URLs redirect into the dashboard so
          existing links keep working — except public certificate verification,
          which stays reachable for third parties (shareable by design). */}
      <Route path="/academy/certificates/:uuid" component={() => <AcademyCertificateVerify />} />
      <Route path="/academy" component={() => <Redirect to="/admin/academy" />} />
      <Route path="/academy/:slug/:lessonSlug" component={(p: any) => <Redirect to={`/admin/academy/${p.params.slug}/${p.params.lessonSlug}`} />} />
      <Route path="/academy/:slug" component={(p: any) => <Redirect to={`/admin/academy/${p.params.slug}`} />} />
      <Route path="/page/:slug" component={() => <InfoPage />} />
      {/* Legal pages: wrap in StoreLayout so the tenant storefront's
          header (navbar) and footer are shown around the LegalShell.
          Customers reported reaching e.g. /terms-of-service from the
          footer link and finding a chrome-less page with no way back
          into the catalogue. LegalShell only renders a small "back to
          home" button — it does not replace the storefront chrome. */}
      <Route path="/privacy-policy" component={() => <LegalGate><PrivacyPolicy /></LegalGate>} />
      <Route path="/privacy" component={() => <LegalGate><PrivacyPolicy /></LegalGate>} />
      <Route path="/terms-of-service" component={() => <LegalGate><TermsOfService /></LegalGate>} />
      <Route path="/terms" component={() => <LegalGate><TermsOfService /></LegalGate>} />
      <Route path="/data-deletion" component={() => <LegalGate><DataDeletion /></LegalGate>} />
      <Route path="/cookies" component={() => <LegalGate><CookiePolicy /></LegalGate>} />
      <Route path="/cookie-policy" component={() => <LegalGate><CookiePolicy /></LegalGate>} />
      <Route path="/acceptable-use" component={() => <LegalGate><AcceptableUse /></LegalGate>} />
      <Route path="/aup" component={() => <LegalGate><AcceptableUse /></LegalGate>} />
      <Route path="/merchant-agreement" component={() => <LegalGate><MerchantAgreement /></LegalGate>} />
      <Route path="/security" component={() => <LegalGate><SecurityStatement /></LegalGate>} />
      <Route path="/partners-terms" component={() => <LegalGate><PartnerAgreement /></LegalGate>} />
      <Route path="/partner-agreement" component={() => <LegalGate><PartnerAgreement /></LegalGate>} />
      <Route path="/security-statement" component={() => <SecurityStatement />} />
      <Route path="/invoice/:id" component={() => <Invoice />} />
      <Route path="/credit-note/:id" component={() => <CreditNote />} />
      <Route path="/shipping-label/:id" component={() => <ShippingLabel />} />
      <Route path="/packing-list/:id" component={() => <PackingList />} />
      <Route path="/print/order-item/:orderId/:itemId/:mode" component={() => <PrintOrderItem />} />

      <Route path="/pricing" component={() => <PlatformOnlyRoute path="/pricing" component={Pricing} />} />
      <Route path="/signup" component={() => <PlatformOnlyRoute path="/signup" component={Signup} />} />
      <Route path="/trial" component={TrialSignup} />
      <Route path="/trial-signup" component={TrialSignup} />
      <Route path="/onboarding" component={Onboarding} />
      <Route path="/platform-admin/pending-approvals/:id" component={PlatformPendingApprovalDetail} />
      <Route path="/platform-admin/pending-approvals" component={PlatformPendingApprovals} />
      <Route path="/platform-admin/document-types" component={PlatformDocumentTypes} />
      <Route path="/platform-admin/trial-settings" component={PlatformTrialSettings} />
      <Route path="/platform-admin/brand-assets" component={PlatformBrandAssets} />
      <Route path="/platform-admin/nexus-store" component={PlatformNexusStore} />
      <Route path="/platform-admin/nexus-store-categories" component={PlatformNexusStoreCategories} />
      <Route path="/platform-admin/theme-review/:id" component={PlatformThemeReviewDetail} />
      <Route path="/platform-admin/theme-review" component={PlatformThemeReview} />
      <Route path="/platform-admin/login" component={PlatformLogin} />
      <Route path="/platform-admin" component={PlatformDashboard} />
      <Route path="/platform-admin/merchants" component={PlatformMerchants} />
      <Route path="/platform-admin/merchants/:id" component={PlatformMerchantDetail} />
      <Route path="/platform-admin/zatca" component={PlatformZatca} />
      <Route path="/platform-admin/packages" component={PlatformPackages} />
      <Route path="/platform-admin/storage-usage" component={PlatformStorageUsage} />
      <Route path="/platform-admin/error-logs" component={PlatformErrorLogs} />
      <Route path="/platform-admin/scheduled-jobs" component={PlatformScheduledJobs} />
      <Route path="/platform-admin/shipping-billing" component={PlatformShippingBilling} />
      <Route path="/platform-admin/system-performance" component={PlatformSystemPerformance} />
      <Route path="/platform-admin/cache-management" component={PlatformCacheManagement} />
      <Route path="/platform-admin/invoices" component={PlatformInvoices} />
      <Route path="/platform-admin/message-templates" component={PlatformMessageTemplates} />
      <Route path="/platform-admin/audit" component={PlatformAudit} />
      <Route path="/platform-admin/customers" component={PlatformCustomers} />
      <Route path="/platform-admin/blog" component={PlatformBlogPosts} />
      <Route path="/platform-admin/blog/categories" component={PlatformBlogCategories} />
      <Route path="/platform-admin/blog/settings" component={PlatformBlogSettings} />
      <Route path="/platform-admin/blog/posts/:id" component={PlatformBlogPostEdit} />
      <Route path="/platform-admin/payment-providers" component={PlatformPaymentProviders} />
      <Route path="/platform-admin/shipping-providers" component={PlatformShippingProviders} />
      <Route path="/platform-admin/pos-integrations" component={PlatformPosIntegrations} />
      <Route path="/platform-admin/payouts" component={PlatformPayouts} />
      <Route path="/platform-admin/theme-earnings" component={PlatformThemeEarnings} />
      <Route path="/platform-admin/backups" component={PlatformBackups} />
      <Route path="/platform-admin/meeting-settings" component={PlatformMeetingSettings} />
      <Route path="/platform-admin/meeting-questions" component={PlatformMeetingQuestions} />
      <Route path="/platform-admin/meeting-bookings" component={PlatformMeetingBookings} />
      <Route path="/platform-admin/meeting-marketers" component={PlatformMeetingMarketers} />
      <Route path="/book-meeting" component={BookMeetingGate} />
      <Route path="/platform-admin/help-overrides" component={PlatformHelpOverrides} />
      <Route path="/platform-admin/email-suppressions" component={PlatformEmailSuppressions} />
      <Route path="/platform-admin/team-members" component={PlatformTeamMembers} />
      <Route path="/platform-admin/community" component={PlatformCommunity} />
      <Route path="/platform-admin/roadmap" component={PlatformRoadmap} />
      <Route path="/platform-admin/trust-signals" component={PlatformTrustSignals} />
      <Route path="/admin/community" component={() => <AdminLayout><AdminCommunity /></AdminLayout>} />
      <Route path="/admin/community/:slug" component={() => <AdminLayout><AdminCommunityPost /></AdminLayout>} />
      <Route path="/admin/roadmap" component={() => <AdminLayout><AdminRoadmap /></AdminLayout>} />
      <Route path="/roadmap" component={() => <PlatformOnlyRoute path="/roadmap" component={RoadmapPage} />} />
      <Route path="/platform-admin/system-monitoring" component={PlatformSystemMonitoring} />
      <Route path="/platform-admin/seo" component={PlatformSeo} />
      <Route path="/platform-admin/partners" component={PlatformPartners} />
      <Route path="/partners" component={PartnerAuth} />
      <Route path="/partners/dashboard" component={PartnerDashboard} />
      <Route path="/admin/payment-methods-v2" component={PaymentMethodsV2Redirect} />
      <Route path="/admin/shipping-providers-v2" component={() => <AdminLayout><AdminShippingProvidersV2 /></AdminLayout>} />
      <Route path="/admin/pos-providers" component={() => <AdminLayout><AdminPosProviders /></AdminLayout>} />
      <Route path="/admin/integrations/pos" component={() => <AdminLayout><AdminIntegrationsPos /></AdminLayout>} />
      <Route path="/admin/carrier-rules" component={() => <AdminLayout><AdminCarrierRules /></AdminLayout>} />
      <Route path="/admin/shipping/oto/reports" component={() => <AdminLayout><AdminShippingOtoReports /></AdminLayout>} />
      <Route path="/admin/wallet" component={WalletToAccountingRedirect} />

      {/* Legacy /platform/* — redirect to /platform-admin/* for back-compat */}
      <Route path="/platform/:rest*" component={PlatformLegacyRedirect} />
      <Route path="/platform" component={PlatformLegacyRedirect} />

      <Route component={NotFoundGate} />
    </Switch>
    </Suspense>
    </CurrentLayout>
    </>
  );
}

function App() {
  // Phase 2: capture per-tenant ?ref=AFF-XXXX / ?ref=REF-XXXX once on first
  // load. Persists to localStorage with a 30-day TTL; the captured code is
  // re-read at checkout time to attribute affiliate commissions / referral
  // rewards. Independent from the platform-level nx_partner_ref used on
  // /signup (Phase 1).
  useEffect(() => {
    captureStoreRef();
  }, []);

  // View Transitions wrapping — INTENTIONALLY DISABLED (May 2026).
  //
  // Historical context: this useEffect used to monkey-patch
  // history.pushState/replaceState to wrap every navigation in
  // document.startViewTransition(), giving a 160 ms compositor-level
  // cross-fade that masked the StoreLayout/AdminLayout unmount-remount
  // flash. It was Layer 4 of the original 5-layer flash defense.
  //
  // Why disabled: the architectural fix (hoisting StoreLayout above
  // <Switch> via CurrentLayout — see line ~1075) made the chrome
  // persistent. The cross-fade now blends two IDENTICAL header/footer
  // snapshots on every nav, which the user perceives AS the flash it
  // was meant to hide (May 2026: "لا زال الوميض موجود" report on
  // luxurygive.com). With chrome persistent, only the content slot
  // changes — and the content slot is already protected by Suspense
  // (ContentSlotFallback) + useDeferredValue + idle preloaders, which
  // together keep content swaps visually instantaneous.
  //
  // If a future regression brings flash back, prefer scoping view
  // transitions to a SPECIFIC element via `view-transition-name`
  // CSS rather than the root snapshot — that way only the changing
  // region cross-fades, not the (now identical) chrome.
  useEffect(() => {
    return;
  }, []);

  // Idle-time chunk preloading — fires all registered lazy() factories in
  // the background after the page is idle, so subsequent navigations never
  // hit a Suspense fallback (kills the "page → blank → page" flash on link
  // clicks). Done after a small delay so it never competes with LCP / API
  // calls needed for the first paint. Uses requestIdleCallback when
  // available; falls back to setTimeout on Safari which still lacks support.
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      try {
        for (const p of PRELOADERS) {
          try { p().catch(() => {}); } catch {}
        }
      } catch {}
    };
    const ric = (window as any).requestIdleCallback as undefined | ((cb: () => void, opts?: { timeout: number }) => number);
    const handle: any = ric
      ? ric(run, { timeout: 4000 })
      : setTimeout(run, 2500);
    return () => {
      cancelled = true;
      try {
        if (ric && (window as any).cancelIdleCallback) (window as any).cancelIdleCallback(handle);
        else clearTimeout(handle);
      } catch {}
    };
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <I18nProvider>
          <CurrencyProvider>
          <ThemeProvider>
          <ThemeEngineProvider>
          <DarkModeProvider>
          <AuthProvider>
            <TurnstileProvider>
            <HelpModeProvider>
            <EntitlementsProvider>
            <WishlistProvider>
              <CompareProvider>
                <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
                  <ScrollToTop />
                  <RiyalSymbolSwapper />
                  <PageTracker />
                  <VisitorHeartbeat />
                  <CustomerVisitTracker />
                  <StoreTitleSync />
                  <ChunkErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <Router />
                    </Suspense>
                  </ChunkErrorBoundary>
                  <Suspense fallback={null}>
                    <CommandPalette />
                  </Suspense>
                  <CompareBar />
                  {/* MobileBottomNav is rendered by StoreLayout for tenant
                      stores only — never on the platform welcome page or
                      platform admin/blog routes. Do not render it here. */}
                </WouterRouter>
                <Toaster />
                <PushOptIn />
                <SubscriptionGate />
              </CompareProvider>
            </WishlistProvider>
            </EntitlementsProvider>
            </HelpModeProvider>
            </TurnstileProvider>
          </AuthProvider>
          </DarkModeProvider>
          </ThemeEngineProvider>
          </ThemeProvider>
          </CurrencyProvider>
        </I18nProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
